import { useEffect, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Badge } from "../components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table"
import { Label } from "../components/ui/label"
import { MoonLoader } from "../components/ui/moon-loader"
import { 
  ShoppingCart, Search, User, CreditCard, DollarSign, 
  Plus, Minus, X, Check, Calculator, Receipt, 
  Users, Package, Tag, Percent, FileText, Clock,
  Trash2, Edit3, Edit, Banknote, Smartphone, Printer, Mail,
  Scan, Info, Star, TrendingUp, Filter, Camera,
  Video, VideoOff, Keyboard, AlertCircle, History,
  ChevronDown, ChevronUp, Eye, Send, Download,
  CheckCircle, CheckCircle2, XCircle, ExternalLink, Copy, Link
} from "lucide-react"
import { useToast } from "../hooks/use-toast"
import apiService from "../lib/api"
import { inventoryApi, type Product } from "../lib/api/inventory"
import { useAuth } from "../contexts/auth-context"
import { getCompanyId, getApiUrl, getTenantId } from "../lib/config"
import { TranslatedText } from '../components/translated-text'
import { useTranslation, useTranslatedText } from '../hooks/useTranslation'
import { Receipt as ReceiptComponent, type ReceiptData } from "../components/Receipt"
import { ReceiptManager } from "../lib/receipt-manager"
import { InvoicePDFGenerator } from "../lib/invoice-pdf-generator"
import { bankingApi, type BankAccount } from "../lib/api/banking"

// List of banks operating in Rwanda for customer payment methods
const CUSTOMER_PAYMENT_BANKS = [
  'Bank of Kigali',
  'Equity Bank',
  'Ecobank Rwanda',
  'I&M Bank Rwanda',
  'Bank Populaire du Rwanda',
  'Access Bank Rwanda',
  'GT Bank Rwanda',
  'Urwego Bank',
  'Zigama Credit and Savings Bank',
  'Development Bank of Rwanda'
];

// POS specific types - using inventory Product interface
type POSProduct = Product

interface POSCartItem {
  product: POSProduct
  quantity: number
  unitPrice: number
  discount: number
  taxRate: number
  lineTotal: number
}

interface POSCustomer {
  id: string
  name: string
  email?: string
  phone?: string
}

interface POSInvoice {
  id: string
  companyId: string
  customerId: string
  invoiceNumber: string
  issueDate: string
  dueDate?: string
  totalAmount: number
  balanceDue: number
  status: string
  currency?: string
  bankAccountId?: string
  lines?: POSInvoiceLine[]
}

interface POSInvoiceLine {
  id: string
  productId: string
  description: string
  quantity: number
  unitPrice: number
  taxRate: number
  discountRate: number
  lineTotal: number
  product?: POSProduct
}

export default function POSPage() {
  const { toast } = useToast()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { tSync } = useTranslation()
  const queryClient = useQueryClient()
  
  // Translated placeholders that need to update reactively
  const searchPlaceholder = useTranslatedText("Search products, SKU, barcode...")
  
  // POS State
  const [selectedCompany, setSelectedCompany] = useState<string>(getCompanyId())
  const [cart, setCart] = useState<POSCartItem[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<POSCustomer | null>(null)
  const [productSearch, setProductSearch] = useState("")
  const [customerSearch, setCustomerSearch] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [showProductInfo, setShowProductInfo] = useState<POSProduct | null>(null)
  const [favoriteProducts, setFavoriteProducts] = useState<string[]>([])
  const [isScanning, setIsScanning] = useState(false)
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
  const [manualBarcode, setManualBarcode] = useState('')
  
  // Loading states for invoice actions
  const [markingAsSent, setMarkingAsSent] = useState<string | null>(null)
  const [markingAsPaid, setMarkingAsPaid] = useState<string | null>(null)
  
  // Invoice Edit and Delete State
  const [editingInvoice, setEditingInvoice] = useState<any>(null)
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const [invoiceForm, setInvoiceForm] = useState<{ customerId: string; invoiceNumber: string; currency: string; issueDate: string; dueDate?: string; discountMode: 'amount'|'percent'; discount: number; shipping: number; taxMode: 'per_line'|'global'; globalTaxRate: number; lines: any[]; subtotal: number; taxTotal: number; totalAmount: number; notes?: string; terms?: string; paymentTerms?: string }>({
    customerId: "",
    invoiceNumber: "",
    currency: "USD",
    issueDate: new Date().toISOString().slice(0,10),
    dueDate: "",
    discountMode: 'amount',
    discount: 0,
    shipping: 0,
    taxMode: 'per_line',
    globalTaxRate: 0,
    lines: [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "" }],
    subtotal: 0,
    taxTotal: 0,
    totalAmount: 0,
    notes: "",
    terms: "Payment due within 30 days",
    paymentTerms: "Net 30"
  })
  const [invoiceSaving, setInvoiceSaving] = useState(false)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [invoiceToDelete, setInvoiceToDelete] = useState<any>(null)
  
  // Currency Conversion State
  const [convertedProductPrices, setConvertedProductPrices] = useState<Record<string, number>>({})
  const [convertedInvoiceAmounts, setConvertedInvoiceAmounts] = useState<Record<string, { totalAmount: number; balanceDue: number }>>({})
  const [convertingCurrency, setConvertingCurrency] = useState<Record<string, boolean>>({})
  const [deletingInvoice, setDeletingInvoice] = useState(false)

  // Listen for company changes from header
  useEffect(() => {
    const handleStorageChange = () => {
      const newCompanyId = getCompanyId();
      if (newCompanyId && newCompanyId !== selectedCompany) {
        setSelectedCompany(newCompanyId);
        // Clear cart when switching companies
        setCart([]);
        setSelectedCustomer(null);
      }
    };

    // Listen for localStorage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (in case localStorage doesn't trigger)
    const handleCompanyChange = (e: CustomEvent) => {
      const newCompanyId = e.detail.companyId;
      if (newCompanyId && newCompanyId !== selectedCompany) {
        setSelectedCompany(newCompanyId);
        // Clear cart when switching companies
        setCart([]);
        setSelectedCustomer(null);
      }
    };
    
    window.addEventListener('companyChanged', handleCompanyChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener);
    };
  }, [selectedCompany]);
  const [scannerError, setScannerError] = useState<string | null>(null)
  
  // UI State
  const [showCustomerDialog, setShowCustomerDialog] = useState(false)
  const [showPaymentDialog, setShowPaymentDialog] = useState(false)
  const [showDiscountDialog, setShowDiscountDialog] = useState(false)
  const [showReceiptDialog, setShowReceiptDialog] = useState(false)
  const [showBarcodeDialog, setShowBarcodeDialog] = useState(false)
  const [showInvoiceViewDialog, setShowInvoiceViewDialog] = useState(false)
  const [selectedInvoiceForView, setSelectedInvoiceForView] = useState<POSInvoice | null>(null)
  const [posInvoiceReconciliationStatus, setPosInvoiceReconciliationStatus] = useState<{
    isReconciled: boolean
    reconciledPayments: number
    totalPayments: number
    bankTransactions: Array<{ id: string; amount: number; date: string; description: string }>
  } | null>(null)
  
  // Customer creation state
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<any>(null)
  const [customerForm, setCustomerForm] = useState<{ name: string; email?: string; phone?: string; tin?: string; currency?: string }>({ name: "", email: "", phone: "", tin: "", currency: 'Frw' })
  const [customerSaving, setCustomerSaving] = useState(false)
  const [customerError, setCustomerError] = useState<string | null>(null)
  const [showReceiptEmailDialog, setShowReceiptEmailDialog] = useState(false)
  const [receiptEmailTo, setReceiptEmailTo] = useState('')
  const [isSendingReceiptEmail, setIsSendingReceiptEmail] = useState(false)
  const [lastTransaction, setLastTransaction] = useState<any>(null)
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null)
  
  // Customer selection state
  const [recentCustomerIds, setRecentCustomerIds] = useState<string[]>([])
  const [customerSearchFocused, setCustomerSearchFocused] = useState(false)
  
  // Payment State
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'mobile'>('card')
  const [cashReceived, setCashReceived] = useState<number>(0)
  const [processing, setProcessing] = useState(false)
  const [selectedBankAccount, setSelectedBankAccount] = useState<string>('')
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false)
  
  // Customer bank search state for payment methods
  const [customerBankSearchTerm, setCustomerBankSearchTerm] = useState('')
  const [filteredCustomerBanks, setFilteredCustomerBanks] = useState<string[]>(CUSTOMER_PAYMENT_BANKS)
  const [selectedCustomerBank, setSelectedCustomerBank] = useState<string>('')
  
  // Discount State
  const [globalDiscount, setGlobalDiscount] = useState(0)
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>('percent')

  // Recent Invoices State
  const [showRecentInvoices, setShowRecentInvoices] = useState(false)
  const [selectedInvoiceActions, setSelectedInvoiceActions] = useState<string | null>(null)

  // Handle editing customer - populate form when editingCustomer changes
  useEffect(() => {
    if (editingCustomer) {
      setCustomerForm({
        name: editingCustomer.name || "",
        email: editingCustomer.email || "",
        phone: editingCustomer.phone || "",
        tin: editingCustomer.taxNumber || "",
        currency: editingCustomer.currency || 'Frw'
      })
    }
  }, [editingCustomer])
  
  const [sendEmailOpen, setSendEmailOpen] = useState(false)
  const [sendEmailTo, setSendEmailTo] = useState('')
  const [sendEmailInvoiceId, setSendEmailInvoiceId] = useState<string | null>(null)
  const [sendEmailLoading, setSendEmailLoading] = useState(false)

  // Cleanup camera stream when component unmounts
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop())
      }
    }
  }, [cameraStream])

  // Keyboard shortcuts for barcode scanner
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // Ctrl+B to open barcode scanner
      if (e.ctrlKey && e.key === 'b' && !showBarcodeDialog) {
        e.preventDefault()
        startBarcodeScanning()
      }
      // Escape to close scanner
      if (e.key === 'Escape' && showBarcodeDialog) {
        closeBarcodeDialog()
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [showBarcodeDialog])

  // Fetch products from inventory (same as inventory page)
  const { data: productsResponse, isLoading: productsLoading } = useQuery({
    queryKey: ['pos-products', selectedCompany],
    queryFn: () => inventoryApi.getProducts({ 
      companyId: selectedCompany,
      page: 1,
      pageSize: 1000
    }),
    enabled: !!selectedCompany && isAuthenticated && !authLoading
  })
  
  // Debug: Log API response and company info
  useEffect(() => {
    // Debug logging removed
    
    if (productsResponse) {
      // Debug logging removed
    }
  }, [productsResponse, selectedCompany, isAuthenticated, authLoading])
  
  const products = productsResponse?.items || []
  
  // Note: Product prices are converted at display time based on customer currency
  // No need for auto-conversion since products don't have currency field
  
  // Debug: Log product stock quantities and statuses
  useEffect(() => {
    if (products.length > 0) {
      const statusCounts = products.reduce((acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1
        return acc
      }, {} as Record<string, number>)
      
      // Debug logging removed
    } else {
      // Debug logging removed
    }
  }, [products])

  // Fetch customers
  const { data: customersResponse, isLoading: customersLoading, refetch: refetchCustomers } = useQuery({
    queryKey: ['pos-customers', selectedCompany],
    queryFn: () => apiService.getCustomers({ companyId: selectedCompany, pageSize: 1000 }),
    enabled: !!selectedCompany && isAuthenticated && !authLoading
  })
  
  const customers = customersResponse?.items || []

  // Fetch recent invoices for quick access
  const { data: recentInvoicesResponse, isLoading: invoicesLoading } = useQuery({
    queryKey: ['pos-recent-invoices', selectedCompany],
    queryFn: async () => {
      const response = await apiService.getInvoices({ 
        page: 1, 
        pageSize: 5, 
        companyId: selectedCompany 
      })
      const raw = response as any
      const invoiceData = raw?.items ?? raw?.invoices ?? raw?.data ?? raw
      return Array.isArray(invoiceData) ? invoiceData as POSInvoice[] : []
    },
    enabled: !!selectedCompany && isAuthenticated && !authLoading
  })
  
  const recentInvoices = recentInvoicesResponse || []

  // Export POS sales data to CSV
  const exportPOSData = async () => {
    try {
      // Fetch all products first to create a lookup map
      const productsResponse = await apiService.getProducts({ companyId: selectedCompany }) as any
      const productsData = productsResponse?.items ?? productsResponse?.products ?? productsResponse?.data ?? productsResponse
      const allProducts = Array.isArray(productsData) ? productsData : []
      
      // Create a map of productId -> product for quick lookup
      const productsMap = new Map<string, any>()
      allProducts.forEach((product: any) => {
        if (product.id) {
          productsMap.set(product.id, product)
        }
      })

      // Fetch all invoices with their lines
      const allInvoicesData: any[] = []
      let page = 1
      const pageSize = 100
      let hasMore = true

      while (hasMore) {
        const response = await apiService.getInvoices({ 
          page, 
          pageSize, 
          companyId: selectedCompany 
        }) as any
        
        const invoiceData = response?.items ?? response?.invoices ?? response?.data ?? response
        const invoices = Array.isArray(invoiceData) ? invoiceData : []
        
        if (invoices.length === 0) {
          hasMore = false
        } else {
          allInvoicesData.push(...invoices)
          
          if (invoices.length < pageSize) {
            hasMore = false
          } else {
            page++
          }
        }
      }

      if (allInvoicesData.length === 0) {
        toast({
          title: 'No Data to Export',
          description: 'There are no invoices to export.',
          variant: 'default'
        })
        return
      }

      // Build export rows from invoice lines
      const exportRows: any[] = []
      
      for (const invoice of allInvoicesData) {
        const invoiceLines = invoice.lines || []
        const invoiceDate = invoice.issueDate ? new Date(invoice.issueDate).toISOString().split('T')[0] : ''
        const invoiceNumber = invoice.invoiceNumber || invoice.id || ''
        
        // Check if invoice uses global tax or per-line tax
        const taxMode = invoice.taxMode || 'per_line'
        const globalTaxRate = Number(invoice.globalTaxRate || 0)
        const invoiceTaxAmount = Number(invoice.taxAmount || 0)
        const invoiceDiscount = Number(invoice.discount || 0)
        const invoiceDiscountMode = invoice.discountMode || 'amount'
        
        // Calculate total line subtotal for distributing global tax
        let totalLineSubtotal = 0
        for (const line of invoiceLines) {
          const qty = Number(line.quantity || 0)
          const price = Number(line.unitPrice || 0)
          const lineDisc = Number(line.lineDiscount || 0)
          const lineSubtotal = qty * price
          const lineTotalAfterDiscount = Math.max(0, lineSubtotal - lineDisc)
          totalLineSubtotal += lineTotalAfterDiscount
        }
        
        // Apply invoice-level discount if applicable
        let totalAfterInvoiceDiscount = totalLineSubtotal
        if (invoiceDiscount > 0) {
          if (invoiceDiscountMode === 'percent') {
            totalAfterInvoiceDiscount = totalLineSubtotal * (1 - invoiceDiscount / 100)
          } else {
            totalAfterInvoiceDiscount = Math.max(0, totalLineSubtotal - invoiceDiscount)
          }
        }
        
        for (const line of invoiceLines) {
          const quantity = Number(line.quantity || 0)
          const unitPrice = Number(line.unitPrice || 0)
          const lineDiscount = Number(line.lineDiscount || 0)
          
          // Calculate line total after discount
          const lineSubtotal = quantity * unitPrice
          const lineTotalAfterDiscount = Math.max(0, lineSubtotal - lineDiscount)
          
          // Calculate tax for this line - matching recalcTotals logic from edit invoice
          // First, try to use the stored taxAmount from the line (most accurate)
          let taxAmount = Number(line.taxAmount || 0)
          
          // If line taxAmount is 0 or not available, calculate it using the same logic as recalcTotals
          if (taxAmount === 0 || isNaN(taxAmount)) {
            if (taxMode === 'global' && globalTaxRate > 0 && totalLineSubtotal > 0) {
              // For global tax: calculate on taxable base (after invoice discount), then distribute proportionally
              const taxableBase = Math.max(0, totalAfterInvoiceDiscount)
              const totalTax = taxableBase * (globalTaxRate / 100)
              const lineShare = lineTotalAfterDiscount / totalLineSubtotal
              taxAmount = totalTax * lineShare
            } else if (taxMode === 'per_line') {
              // For per-line tax: calculate on quantity * unitPrice (before line discount) - matching recalcTotals
              const taxRate = Number(line.taxRate || 0)
              if (taxRate > 0 && !isNaN(taxRate)) {
                // Match recalcTotals: tax is calculated on quantity * unitPrice, not on discounted amount
                taxAmount = quantity * unitPrice * (taxRate / 100)
              } else if (invoiceTaxAmount > 0 && totalLineSubtotal > 0) {
                // Fallback: if line doesn't have taxRate, distribute invoice tax proportionally based on line total
                const lineShare = lineTotalAfterDiscount / totalLineSubtotal
                taxAmount = invoiceTaxAmount * lineShare
              }
            } else if (invoiceTaxAmount > 0 && totalLineSubtotal > 0) {
              // Final fallback: if invoice has taxAmount, distribute it proportionally
              const lineShare = lineTotalAfterDiscount / totalLineSubtotal
              taxAmount = invoiceTaxAmount * lineShare
            }
          }
          
          // Total price includes tax
          const totalPrice = lineTotalAfterDiscount + taxAmount
          
          // Get product code/SKU if available
          let itemCode = ''
          
          if (line.productCode) {
            itemCode = line.productCode
          } else if (line.product?.sku) {
            itemCode = line.product.sku
          } else if (line.product?.code) {
            itemCode = line.product.code
          } else if (line.productId) {
            const product = productsMap.get(line.productId)
            if (product) {
              itemCode = product.sku || product.code || ''
            }
          }
          
          // Fallback: try to match by description
          if (!itemCode && line.description) {
            for (const [_, product] of productsMap.entries()) {
              if (product.name === line.description) {
                itemCode = product.sku || product.code || ''
                break
              }
            }
          }
          
          const itemName = line.description || ''
          
          exportRows.push({
            date: invoiceDate,
            itemCode: itemCode,
            itemName: itemName,
            invoiceNumber: invoiceNumber,
            quantity: quantity.toFixed(2),
            unitPrice: unitPrice.toFixed(2),
            totalPrice: totalPrice.toFixed(2),
            tax: taxAmount.toFixed(2)
          })
        }
      }

      if (exportRows.length === 0) {
        toast({
          title: 'No Data to Export',
          description: 'There are no invoice lines to export.',
          variant: 'default'
        })
        return
      }

      // Create CSV content
      const headers = ['Date', 'Item N./code', 'Item Name', 'Invoice Number', 'Quantity', 'Unit price', 'Total price', 'Tax']
      
      // Helper function to escape CSV cells
      const escapeCSVCell = (value: any): string => {
        if (value === null || value === undefined || value === '') {
          return ''
        }
        let str = String(value).trim()
        if (str === '' || str === 'null' || str === 'undefined') {
          return ''
        }
        // Remove any existing surrounding quotes
        while (str.startsWith('"') && str.endsWith('"') && str.length > 1) {
          str = str.slice(1, -1)
        }
        // Replace double quotes with escaped quotes
        str = str.replace(/"/g, '""')
        // Quote if contains comma, quote, or newline
        if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
          return `"${str}"`
        }
        return str
      }

      const csvContent = [
        headers.map(escapeCSVCell).join(','),
        ...exportRows.map(row => [
          escapeCSVCell(row.date),
          escapeCSVCell(row.itemCode),
          escapeCSVCell(row.itemName),
          escapeCSVCell(row.invoiceNumber),
          escapeCSVCell(row.quantity),
          escapeCSVCell(row.unitPrice),
          escapeCSVCell(row.totalPrice),
          escapeCSVCell(row.tax)
        ].join(','))
      ].join('\n')

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `pos-sales-report-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      toast({
        title: 'Export Successful',
        description: `Exported ${exportRows.length} line items to CSV.`
      })
    } catch (error: any) {
      console.error('Export error:', error)
      toast({
        title: 'Export Failed',
        description: error?.message || 'Failed to export POS data. Please try again.',
        variant: 'destructive'
      })
    }
  }

  // Fetch customers for invoice editing
  const { data: allCustomers = [] } = useQuery({
    queryKey: ['customers', selectedCompany],
    queryFn: async () => {
      const response = await apiService.getCustomers({ companyId: selectedCompany })
      return Array.isArray(response) ? response : (response as any)?.items || []
    },
    enabled: !!selectedCompany
  })

  // Fetch products for invoice editing
  const { data: allProducts = [] } = useQuery({
    queryKey: ['products', selectedCompany],
    queryFn: async () => {
      const response = await inventoryApi.getProducts({ companyId: selectedCompany })
      return Array.isArray(response) ? response : (response as any)?.items || []
    },
    enabled: !!selectedCompany
  })

  // Convert invoice amounts from USD to Frw when currency is USD
  useEffect(() => {
    const convertInvoiceAmounts = async () => {
      if (!recentInvoices || recentInvoices.length === 0) return
      
      const invoicesToConvert = recentInvoices.filter(inv => {
        const customer = allCustomers.find((c: any) => c.id === inv.customerId)
        const currency = (customer as any)?.currency || inv.currency || 'Frw'
        return currency === 'USD' && (inv.totalAmount > 0 || inv.balanceDue > 0)
      })

      for (const inv of invoicesToConvert) {
        if (convertingCurrency[inv.id]) continue
        
        setConvertingCurrency(prev => ({ ...prev, [inv.id]: true }))
        try {
          const conversions = await Promise.all([
            inv.totalAmount > 0 ? bankingApi.convertCurrency(inv.totalAmount, 'USD', 'FRW').catch(() => null) : Promise.resolve(null),
            inv.balanceDue > 0 ? bankingApi.convertCurrency(inv.balanceDue, 'USD', 'FRW').catch(() => null) : Promise.resolve(null)
          ])
          
          const convertedTotal = conversions[0]?.conversion?.convertedAmount || conversions[0]?.convertedAmount || inv.totalAmount
          const convertedBalance = conversions[1]?.conversion?.convertedAmount || conversions[1]?.convertedAmount || inv.balanceDue
          
          setConvertedInvoiceAmounts(prev => ({
            ...prev,
            [inv.id]: { totalAmount: convertedTotal, balanceDue: convertedBalance }
          }))
        } catch (error) {
          console.error('Invoice conversion error:', error)
          setConvertedInvoiceAmounts(prev => ({
            ...prev,
            [inv.id]: { totalAmount: inv.totalAmount, balanceDue: inv.balanceDue }
          }))
        } finally {
          setConvertingCurrency(prev => ({ ...prev, [inv.id]: false }))
        }
      }
    }

    convertInvoiceAmounts()
  }, [recentInvoices, allCustomers])

  // Calculate totals function (matching sales.tsx)
  const recalcTotals = (lines: any[], discount = invoiceForm.discount, shipping = invoiceForm.shipping, taxMode = invoiceForm.taxMode, globalTaxRate = invoiceForm.globalTaxRate, discountMode = invoiceForm.discountMode) => {
    const subtotal = lines.reduce((sum, line) => {
      const lineTotal = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0) - (Number(line.lineDiscount) || 0)
      return sum + lineTotal
    }, 0)
    
    // Ensure discount is always positive (display already shows minus sign)
    const discountAmount = discountMode === 'percent' 
      ? (subtotal * Math.abs(Number(discount) || 0) / 100) 
      : Math.abs(Number(discount) || 0)
    const taxableAmount = subtotal - discountAmount + (Number(shipping) || 0)
    
    const taxAmount = taxMode === 'global' 
      ? taxableAmount * ((Number(globalTaxRate) || 0) / 100)
      : lines.reduce((sum, line) => {
          const lineTotal = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0) - (Number(line.lineDiscount) || 0)
          return sum + (lineTotal * ((Number(line.taxRate) || 0) / 100))
        }, 0)
    
    const totalAmount = subtotal - discountAmount + (Number(shipping) || 0) + taxAmount
    
    return {
      subtotal: Number(subtotal),
      taxTotal: Number(taxAmount),
      totalAmount: Number(totalAmount),
      discount: Number(discountAmount)
    }
  }

  // Populate invoice form when editing (matching sales.tsx)
  useEffect(() => {
    if (editingInvoice && invoiceDialogOpen) {
      // Convert the invoice data to the form format
      const formData = {
        customerId: editingInvoice.customerId || "",
        invoiceNumber: editingInvoice.invoiceNumber || "",
        currency: editingInvoice.currency || "Frw",
        issueDate: editingInvoice.issueDate ? new Date(editingInvoice.issueDate).toISOString().slice(0,10) : new Date().toISOString().slice(0,10),
        dueDate: editingInvoice.dueDate ? new Date(editingInvoice.dueDate).toISOString().slice(0,10) : "",
        discountMode: 'amount' as 'amount'|'percent',
        discount: Number(editingInvoice.discountAmount) || 0,
        shipping: Number(editingInvoice.shippingAmount) || 0,
        taxMode: 'per_line' as 'per_line'|'global',
        globalTaxRate: 0,
        lines: editingInvoice.lines?.map((line: any) => ({
          description: line.description || "",
          quantity: Number(line.quantity) || 1,
          unitPrice: Number(line.unitPrice) || 0,
          taxRate: Number(line.taxRate) || 0,
          productId: line.productId || "",
          lineDiscount: Number(line.discountRate) || 0
        })) || [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }],
        notes: editingInvoice.notes || "",
        terms: editingInvoice.terms || "Payment due within 30 days",
        paymentTerms: editingInvoice.paymentTerms || "Net 30",
        subtotal: Number(editingInvoice.subtotal) || 0,
        taxTotal: Number(editingInvoice.taxAmount) || 0,
        totalAmount: Number(editingInvoice.totalAmount) || 0
      }
      
      setInvoiceForm(formData)
    } else if (!editingInvoice && invoiceDialogOpen) {
      // Reset form for new invoice
      const startLines = [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]
      setInvoiceForm({
        customerId: "",
        invoiceNumber: "",
        currency: "Frw",
        issueDate: new Date().toISOString().slice(0,10),
        dueDate: "",
        discountMode: 'amount' as 'amount'|'percent',
        discount: 0,
        shipping: 0,
        taxMode: 'per_line' as 'per_line'|'global',
        globalTaxRate: 0,
        lines: startLines,
        notes: "",
        terms: "Payment due within 30 days",
        paymentTerms: "Net 30",
        subtotal: 0,
        taxTotal: 0,
        totalAmount: 0
      })
    }
  }, [editingInvoice, invoiceDialogOpen])

  // Use the selected invoice for view
  const detailedInvoice = selectedInvoiceForView
  const safeProducts = Array.isArray(products) ? products : []
  const filteredProducts = safeProducts.filter((product: POSProduct) => {
    const matchesSearch = product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(productSearch.toLowerCase())) ||
      (product.barcode && product.barcode.toLowerCase().includes(productSearch.toLowerCase())) ||
      (product.categoryObject?.name && product.categoryObject.name.toLowerCase().includes(productSearch.toLowerCase()))
    
    // Show active and inactive products, exclude discontinued (handle both cases)
    const normalizedStatus = product.status?.toUpperCase()
    const isAvailableProduct = normalizedStatus === 'ACTIVE' || normalizedStatus === 'INACTIVE'
    
    // Filter by category
    const matchesCategory = !selectedCategory || product.categoryId === selectedCategory
    
    // Filter by stock availability - only show products with stock
    const availableQty = Number(product.availableQuantity) || 0
    const stockQty = Number(product.stockQuantity) || 0
    const hasStock = availableQty > 0 || stockQty > 0
    
    // Show available products with stock - inactive products can be reactivated in POS
    return matchesSearch && isAvailableProduct && matchesCategory && hasStock
  })
  
  // Debug: Log filtering results
  useEffect(() => {
    // Debug logging removed
  }, [safeProducts, filteredProducts, productSearch, selectedCategory])

  // Extract unique categories from all available products (active and inactive)
  const categories = Array.from(new Set(
    safeProducts
      .filter(p => {
        const normalizedStatus = p.status?.toUpperCase()
        const isAvailableProduct = normalizedStatus === 'ACTIVE' || normalizedStatus === 'INACTIVE'
        return p.categoryObject?.name && p.categoryId && isAvailableProduct
      })
      .map(p => ({ id: p.categoryId!, name: p.categoryObject!.name }))
  )).sort((a, b) => a.name.localeCompare(b.name))

  // Get favorite products (most frequently added to cart) - available ones
  const favoriteProductsList = safeProducts
    .filter(p => {
      const normalizedStatus = p.status?.toUpperCase()
      const isAvailableProduct = normalizedStatus === 'ACTIVE' || normalizedStatus === 'INACTIVE'
      return favoriteProducts.includes(p.id) && isAvailableProduct
    })
    .slice(0, 6)

  // Filter customers based on search (enhanced with phone number and better matching)
  const filteredCustomers = customers.filter((customer: POSCustomer) => {
    const searchTerm = customerSearch.toLowerCase()
    return (
      customer.name.toLowerCase().includes(searchTerm) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm)) ||
      (customer.phone && customer.phone.toLowerCase().includes(searchTerm))
    )
  })

  // Get recent customers (last 5 selected customers)
  const recentCustomers = customers.filter((customer: POSCustomer) => 
    recentCustomerIds.includes(customer.id)
  ).slice(0, 5)

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => {
    const lineSubtotal = (item.quantity * item.unitPrice) - item.discount
    
    // If product is tax inclusive, we need to extract the tax from the price
        if (item.product.taxInclusive && item.product.taxRate && item.product.taxRate > 0) {
      // For tax inclusive: net price = gross price / (1 + tax rate/100)
        const netPrice = lineSubtotal / (1 + ((item.product.taxRate || 0) / 100))
      return sum + netPrice
    } else {
      // For tax exclusive: use the line subtotal as is
      return sum + lineSubtotal
    }
  }, 0)
  
  // Ensure discount is always positive (display already shows minus sign)
  const discountAmount = discountType === 'percent' 
    ? (subtotal * Math.abs(globalDiscount) / 100) 
    : Math.abs(globalDiscount)
  
  const taxAmount = cart.reduce((sum, item) => {
    const lineSubtotal = (item.quantity * item.unitPrice) - item.discount
    
        if (item.product.taxInclusive && item.product.taxRate && item.product.taxRate > 0) {
      // For tax inclusive: tax amount = gross price - net price
        const netPrice = lineSubtotal / (1 + ((item.product.taxRate || 0) / 100))
      return sum + (lineSubtotal - netPrice)
    } else {
      // For tax exclusive: add tax on top
        return sum + (lineSubtotal * ((item.product.taxRate || 0) / 100))
    }
  }, 0)
  
  const total = subtotal - discountAmount + taxAmount

  // Add product to cart
  const addToCart = (product: POSProduct) => {
    // Use the same stock calculation logic as getStockDisplay
    const availableQty = Number(product.availableQuantity) || 0
    const stockQty = Number(product.stockQuantity) || 0
    const availableStock = availableQty > 0 ? availableQty : stockQty
    
    const existingItemIndex = cart.findIndex(item => item.product.id === product.id)
    
    // Check stock availability (different logic for services vs products)
    const currentQuantityInCart = existingItemIndex >= 0 ? cart[existingItemIndex].quantity : 0
    if (currentQuantityInCart >= availableStock) {
      const isServiceProduct = isService(product)
      toast({
        title: isServiceProduct ? "Service Unavailable" : "Insufficient Stock",
        description: isServiceProduct 
          ? `No more slots available for ${product.name}${availableStock === 0 ? ' (Fully Booked)' : ''}`
          : `Only ${availableStock} units available for ${product.name}`,
        variant: "destructive"
      })
      return
    }
    
    if (existingItemIndex >= 0) {
      const newCart = [...cart]
      newCart[existingItemIndex].quantity += 1
      newCart[existingItemIndex].lineTotal = 
        (newCart[existingItemIndex].quantity * newCart[existingItemIndex].unitPrice) - newCart[existingItemIndex].discount
      setCart(newCart)
    } else {
      // Use costPrice as fallback when unitPrice is 0 or missing
      const sellingPrice = (product.unitPrice && product.unitPrice > 0) 
        ? product.unitPrice 
        : (product.costPrice && product.costPrice > 0 ? product.costPrice : 0)
      
      const newItem: POSCartItem = {
        product,
        quantity: 1,
        unitPrice: sellingPrice,
        discount: 0,
        taxRate: product.taxRate || 0, // Use product's actual tax rate
        lineTotal: sellingPrice
      }
      setCart([...cart, newItem])
    }
  }

  // Update cart item quantity
  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(productId)
      return
    }

    // Find the cart item and check stock
    const cartItem = cart.find(item => item.product.id === productId)
    if (cartItem) {
      const availableStock = cartItem.product.availableQuantity || cartItem.product.stockQuantity || 0
      if (newQuantity > availableStock) {
        toast({
          title: "Insufficient Stock",
          description: `Only ${availableStock} units available for ${cartItem.product.name}`,
          variant: "destructive"
        })
        return
      }
    }
    
    const newCart = cart.map(item => {
      if (item.product.id === productId) {
        return {
          ...item,
          quantity: newQuantity,
          lineTotal: (newQuantity * item.unitPrice) - item.discount
        }
      }
      return item
    })
    setCart(newCart)
  }

  // Remove from cart
  const removeFromCart = (productId: string) => {
    setCart(cart.filter(item => item.product.id !== productId))
  }

  // Clear cart
  const clearCart = () => {
    setCart([])
    setSelectedCustomer(null)
    setGlobalDiscount(0)
  }

  // Create invoice mutation
  const createInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCustomer) {
        throw new Error('Please select a customer')
      }
      
      const invoiceData = {
        companyId: selectedCompany,
        customerId: selectedCustomer.id,
        invoiceNumber: `INV-${Date.now()}`,
        issueDate: new Date().toISOString(),
        currency: (selectedCustomer as any)?.currency || 'Frw',
        lines: cart.map(item => ({
          productId: item.product.id,
          description: item.product.name,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
          discountRate: item.discount // Changed from lineDiscount to discountRate
        })),
        subtotal,
        taxAmount,
        discountAmount,
        shippingAmount: 0,
        totalAmount: total,
        balanceDue: total,
        notes: 'Created via URUTIQ-accounting systems',
        terms: 'Payment due on receipt',
        paymentTerms: 'Due on Receipt',
        status: 'paid',
        paymentMethod: paymentMethod,
        bankAccountId: paymentMethod !== 'cash' ? selectedBankAccount : undefined,
        customerBankName: selectedCustomerBank || undefined
      }
      
      return apiService.createInvoice(invoiceData)
    },
    onSuccess: async (invoice) => {
      try {
        // Invoice is already created as 'paid', so inventory should be updated automatically
        // Get company data for receipt - start with empty defaults
        let companyInfo = {
          name: 'UrutiIQ Business',
          address: '',
          phone: '',
          email: '',
          website: '',
          logoUrl: undefined as string | undefined
        }
        
        try {
          const companyId = selectedCompany || localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
          if (companyId) {
            const company = await apiService.getCompany(companyId) as any
            if (company) {
              // Use company data directly - only fallback to default name if company name is missing
              companyInfo.name = company.name || companyInfo.name
              
              // Check if contact data exists in CompanySettings as fallback
              let email = company.email
              let phone = company.phone
              let website = company.website
              let address = company.address
              let city = company.city
              let state = company.state
              let postalCode = company.postalCode
              
              if (company.companySettings && Array.isArray(company.companySettings)) {
                const emailSetting = company.companySettings.find((s: any) => s.key === 'email')
                const phoneSetting = company.companySettings.find((s: any) => s.key === 'phone')
                const websiteSetting = company.companySettings.find((s: any) => s.key === 'website')
                const addressSetting = company.companySettings.find((s: any) => s.key === 'address')
                
                // Use CompanySettings if Company model fields are null
                if (!email && emailSetting?.value) email = emailSetting.value
                if (!phone && phoneSetting?.value) phone = phoneSetting.value
                if (!website && websiteSetting?.value) website = websiteSetting.value
                if (!address && addressSetting?.value) address = addressSetting.value
              }
              
              // Build full address from company data - use nullish coalescing to avoid defaults
              const addressParts = []
              if (address) addressParts.push(address)
              if (city) addressParts.push(city)
              if (state) addressParts.push(state)
              if (postalCode) addressParts.push(postalCode)
              
              if (addressParts.length > 0) {
                companyInfo.address = addressParts.join(', ')
              } else {
                // If all address fields are null, use empty string
                companyInfo.address = ''
              }
              
              // Always use company data - if null/undefined, use empty string
              // Using ?? ensures null values become '' instead of falling back to defaults
              companyInfo.phone = phone ?? ''
              companyInfo.email = email ?? ''
              companyInfo.website = website ?? ''
              
              // Get logo URL if available
              if (company.logoUrl) {
                let logoUrl = company.logoUrl
                // Convert relative URL to full URL if needed
                if (logoUrl.startsWith('/api/')) {
                  logoUrl = getApiUrl(logoUrl)
                }
                companyInfo.logoUrl = logoUrl
              }
            }
          }
        } catch (error) {
          console.error('❌ Error fetching company data:', error)
          // On error, use fallback defaults
          if (!companyInfo.name || companyInfo.name === 'UrutiIQ Business') {
            companyInfo.name = 'UrutiIQ Business'
            companyInfo.address = '123 Business St, City, State 12345'
            companyInfo.phone = '+1 (555) 123-4567'
            companyInfo.email = 'contact@urutiiq.com'
            companyInfo.website = 'www.urutiiq.com'
          }
        }
        
        // Generate receipt data
        const receipt: ReceiptData = {
          invoiceNumber: invoice.invoiceNumber || `INV-${Date.now()}`,
          date: new Date().toLocaleDateString(),
          customer: selectedCustomer ? {
            name: selectedCustomer.name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone
          } : undefined,
          items: cart.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            total: item.lineTotal
          })),
          subtotal,
          taxAmount,
          discountAmount,
          total,
          paymentMethod,
          cashReceived: paymentMethod === 'cash' ? cashReceived : undefined,
          change: paymentMethod === 'cash' && cashReceived > total ? cashReceived - total : undefined,
          companyInfo
        }
        
        setReceiptData(receipt)
        setLastTransaction(invoice)
        setShowReceiptDialog(true)
        
        toast({
          title: "Sale Completed",
          description: invoice.status === 'paid' 
            ? `Invoice ${invoice.invoiceNumber} processed successfully. Stock updated.`
            : `Invoice ${invoice.invoiceNumber} created successfully.${invoice.status === 'draft' ? ' Stock will be updated when invoice is marked as paid.' : ''}`,
        })
        
        // Invalidate queries to refresh data
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['invoices'] }),
          queryClient.invalidateQueries({ queryKey: ['pos-recent-invoices', selectedCompany] }),
          queryClient.invalidateQueries({ queryKey: ['pos-products', selectedCompany] }),
          queryClient.invalidateQueries({ queryKey: ['products', selectedCompany] }),
          queryClient.invalidateQueries({ queryKey: ['inventory-movements'] }),
          queryClient.invalidateQueries({ queryKey: ['journal-entries'] })
        ])
        
        // Force refetch of POS products to ensure fresh data
        await queryClient.refetchQueries({ queryKey: ['pos-products', selectedCompany] })
        
        clearCart()
        setShowPaymentDialog(false)
        
      } catch (error: any) {
        console.error('Error in POS success handler:', error)
        
        // Get company data for receipt - start with empty defaults
        let companyInfo = {
          name: 'UrutiIQ Business',
          address: '',
          phone: '',
          email: '',
          website: '',
          logoUrl: undefined as string | undefined
        }
        
        try {
          const companyId = selectedCompany || localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
          if (companyId) {
            const company = await apiService.getCompany(companyId) as any
            if (company) {
              // Use company data directly - only fallback to default name if company name is missing
              companyInfo.name = company.name || companyInfo.name
              
              // Check if contact data exists in CompanySettings as fallback
              let email = company.email
              let phone = company.phone
              let website = company.website
              let address = company.address
              let city = company.city
              let state = company.state
              let postalCode = company.postalCode
              
              if (company.companySettings && Array.isArray(company.companySettings)) {
                const emailSetting = company.companySettings.find((s: any) => s.key === 'email')
                const phoneSetting = company.companySettings.find((s: any) => s.key === 'phone')
                const websiteSetting = company.companySettings.find((s: any) => s.key === 'website')
                const addressSetting = company.companySettings.find((s: any) => s.key === 'address')
                
                // Use CompanySettings if Company model fields are null
                if (!email && emailSetting?.value) email = emailSetting.value
                if (!phone && phoneSetting?.value) phone = phoneSetting.value
                if (!website && websiteSetting?.value) website = websiteSetting.value
                if (!address && addressSetting?.value) address = addressSetting.value
              }
              
              // Build full address from company data - use nullish coalescing to avoid defaults
              const addressParts = []
              if (address) addressParts.push(address)
              if (city) addressParts.push(city)
              if (state) addressParts.push(state)
              if (postalCode) addressParts.push(postalCode)
              
              if (addressParts.length > 0) {
                companyInfo.address = addressParts.join(', ')
              } else {
                // If all address fields are null, use empty string
                companyInfo.address = ''
              }
              
              // Always use company data - if null/undefined, use empty string
              // Using ?? ensures null values become '' instead of falling back to defaults
              companyInfo.phone = phone ?? ''
              companyInfo.email = email ?? ''
              companyInfo.website = website ?? ''
              
              // Get logo URL if available
              if (company.logoUrl) {
                let logoUrl = company.logoUrl
                // Convert relative URL to full URL if needed
                if (logoUrl.startsWith('/api/')) {
                  logoUrl = getApiUrl(logoUrl)
                }
                companyInfo.logoUrl = logoUrl
              }
            }
          }
        } catch (error) {
          console.error('❌ Error fetching company data:', error)
          // On error, use fallback defaults
          if (!companyInfo.name || companyInfo.name === 'UrutiIQ Business') {
            companyInfo.name = 'UrutiIQ Business'
            companyInfo.address = '123 Business St, City, State 12345'
            companyInfo.phone = '+1 (555) 123-4567'
            companyInfo.email = 'contact@urutiiq.com'
            companyInfo.website = 'www.urutiiq.com'
          }
        }
        
        // Still show receipt even if there's an error
        const receipt: ReceiptData = {
          invoiceNumber: invoice.invoiceNumber || `INV-${Date.now()}`,
          date: new Date().toLocaleDateString(),
          customer: selectedCustomer ? {
            name: selectedCustomer.name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone
          } : undefined,
          items: cart.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            total: item.lineTotal
          })),
          subtotal,
          taxAmount,
          discountAmount,
          total,
          paymentMethod,
          cashReceived: paymentMethod === 'cash' ? cashReceived : undefined,
          change: paymentMethod === 'cash' && cashReceived > total ? cashReceived - total : undefined,
          companyInfo
        }
        
        setReceiptData(receipt)
        setLastTransaction(invoice)
        setShowReceiptDialog(true)
        
        toast({
          title: "Sale Completed",
          description: `Invoice ${invoice.invoiceNumber} created successfully.`,
        })
        
        // Invalidate queries to refresh data even on error
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['invoices'] }),
          queryClient.invalidateQueries({ queryKey: ['pos-products', selectedCompany] }),
          queryClient.invalidateQueries({ queryKey: ['products'] })
        ])
        
        // Force refetch of POS products to ensure fresh data
        await queryClient.refetchQueries({ queryKey: ['pos-products', selectedCompany] })
        
        clearCart()
        setShowPaymentDialog(false)
      }
    },
    onError: (error: any) => {
      toast({
        title: "Sale Failed",
        description: error.message || "Failed to create invoice",
        variant: "destructive"
      })
    }
  })

  // Process payment
  const processPayment = () => {
    if (paymentMethod === 'cash' && cashReceived < total) {
      toast({
        title: "Insufficient Cash",
        description: `Need ${formatCurrency(total - cashReceived)} more`,
        variant: "destructive"
      })
      return
    }
    
    if (paymentMethod !== 'cash' && !selectedBankAccount) {
      toast({
        title: "Bank Account Required",
        description: "Please select a bank account for this payment method",
        variant: "destructive"
      })
      return
    }
    
    createInvoiceMutation.mutate()
  }

  // Barcode scanning functionality
  const startBarcodeScanning = () => {
    setShowBarcodeDialog(true)
    setScannerError(null)
    setManualBarcode('')
  }

  const startCamera = async () => {
    setIsScanning(true)
    setScannerError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      })
      setCameraStream(stream)
      
      // In a real implementation, you would integrate with a barcode scanning library
      // like QuaggaJS or ZXing to scan from the video stream
      toast({
        title: "Camera Ready",
        description: "Point camera at barcode or enter manually below",
      })
    } catch (error) {
      setScannerError("Unable to access camera. Please use manual input.")
      setIsScanning(false)
    }
  }

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop())
      setCameraStream(null)
    }
    setIsScanning(false)
  }

  const processBarcode = (barcode: string) => {
    if (!barcode.trim()) return

    const product = safeProducts.find(p => 
      p.barcode === barcode.trim() || 
      p.sku === barcode.trim()
    )
    
    if (product) {
      addToCart(product)
      toast({
        title: "Product Added",
        description: `${product.name} added to cart`,
      })
      closeBarcodeDialog()
    } else {
      toast({
        title: "Product Not Found",
        description: "No product found with this barcode",
        variant: "destructive"
      })
    }
  }

  const closeBarcodeDialog = () => {
    stopCamera()
    setShowBarcodeDialog(false)
    setManualBarcode('')
    setScannerError(null)
  }

  // Handle invoice view
  const handleViewInvoice = async (invoice: POSInvoice) => {
    setSelectedInvoiceForView(invoice)
    setShowInvoiceViewDialog(true)
    
    // Fetch reconciliation status
    try {
      const payments = await apiService.getInvoicePayments(invoice.id)
      // Get payments with bank transaction links
      const reconciledPayments = payments.filter((p: any) => p.bankTransactionId || p.isReconciled)
      const bankTransactions = reconciledPayments.map((p: any) => ({
        id: p.bankTransactionId,
        amount: p.amount,
        date: p.paymentDate,
        description: p.reference || 'Bank Transaction'
      }))
      
      setPosInvoiceReconciliationStatus({
        isReconciled: reconciledPayments.length > 0,
        reconciledPayments: reconciledPayments.length,
        totalPayments: payments.length,
        bankTransactions
      })
    } catch {
      setPosInvoiceReconciliationStatus(null)
    }
  }

  // Toggle favorite product
  const toggleFavorite = (productId: string) => {
    setFavoriteProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    )
  }

  const formatCurrency = (amount: number, currency: string = 'Frw') => {
    const amt = Number(amount) || 0
    // Format as "Amount Frw" (e.g., "1,000.00 Frw")
    if (currency === 'Frw' || currency === 'FRW' || currency === 'RWF') {
      return `${amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw`
    }
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amt)
    } catch {
      return `$${amt.toFixed(2)}`
    }
  }

  // Helper function to determine if a product is a service
  const isService = (product: POSProduct) => {
    return product.type === 'SERVICE'
  }

  // Helper function to select customer from invoice for quick checkout
  const selectCustomerFromInvoice = (invoice: POSInvoice) => {
    const customer = customers.find(c => c.id === invoice.customerId)
    if (customer) {
      setSelectedCustomer(customer)
      toast({
        title: "Customer Selected",
        description: `${customer.name} selected for checkout. Add items to continue transaction.`,
      })
    }
  }

  // Invoice action handlers
  const handleMarkInvoiceAsSent = async (invoiceId: string) => {
    setMarkingAsSent(invoiceId)
    try {
      await apiService.updateInvoice(invoiceId, { status: 'sent' })
      queryClient.invalidateQueries({ queryKey: ['pos-recent-invoices', selectedCompany] })
      toast({
        title: "Invoice Updated",
        description: "Invoice marked as sent successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update invoice status",
        variant: "destructive"
      })
    } finally {
      setMarkingAsSent(null)
    }
  }

  const handleMarkInvoiceAsPaid = async (invoiceId: string) => {
    setMarkingAsPaid(invoiceId)
    try {
      // First, try to process accounting entries and inventory updates
      const accountingResult = await apiService.processInvoicePayment(invoiceId)
      
      // Update invoice status after successful accounting processing
      await apiService.updateInvoice(invoiceId, { status: 'paid', balanceDue: 0 })
      
      // Update bank balance when invoice is marked as paid
      // Reconciliation will only mark transaction as reconciled (won't update balance again)
      const invoice = recentInvoices.find(inv => inv.id === invoiceId)
      
      // Create bank transaction and update balance immediately
      if (invoice && invoice.bankAccountId) {
        try {
          await bankingApi.createBankTransaction({
            bankAccountId: invoice.bankAccountId,
            transactionDate: new Date().toISOString(),
            amount: invoice.totalAmount,
            transactionType: 'credit',
            description: `POS Invoice Payment - ${invoice.invoiceNumber}`,
            reference: `PAY-${invoice.invoiceNumber}`,
            status: 'unreconciled', // Internal transaction - unreconciled until matched with bank statement
            isReconciled: false, // Internal transaction - unreconciled until matched with bank statement
            category: 'Revenue & Sales',
            updateBalance: true // Update bank balance when invoice is marked as paid
          } as any)
        } catch (bankError) {
          console.error('Failed to create bank transaction:', bankError)
          // Don't fail the entire transaction, just log the error
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['pos-recent-invoices', selectedCompany] })
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] })
      queryClient.invalidateQueries({ queryKey: ["inventory-movements"] })
      queryClient.invalidateQueries({ queryKey: ["bank-accounts"] })
      
      toast({
        title: "Invoice Paid",
        description: `Payment processed successfully. Stock updated.${invoice?.bankAccountId ? ' Bank transaction created (balance will update when bank statement matches).' : ''} Journal Entry: ${accountingResult.journalEntryId}`,
      })
    } catch (error: any) {
      console.error('Error marking invoice as paid:', error)
      
      let errorTitle = "Error"
      let errorDescription = "Failed to mark invoice as paid. Please try again."
      
      if (error.message) {
        if (error.message.includes('Insufficient inventory')) {
          errorTitle = "Insufficient Inventory"
          errorDescription = error.message
        } else if (error.message.includes('Company with ID') && error.message.includes('not found')) {
          errorTitle = "Company Not Found"
          errorDescription = "The company associated with this invoice could not be found. Please contact support."
        } else if (error.message.includes('Failed to create account')) {
          errorTitle = "Account Setup Required"
          errorDescription = "Required accounting accounts are missing. Please ensure the company has proper account setup."
        } else if (error.message.includes('Transaction already closed') || error.message.includes('timeout')) {
          errorTitle = "Processing Timeout"
          errorDescription = "The payment processing took too long. Please try again or contact support if the issue persists."
        } else if (error.message.includes('Failed to process invoice payment')) {
          errorTitle = "Payment Processing Failed"
          errorDescription = error.message.replace('Failed to process invoice payment: ', '')
        } else {
          errorDescription = error.message
        }
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive"
      })
    } finally {
      setMarkingAsPaid(null)
    }
  }

  // Handle editing invoice
  const handleEditInvoice = (invoice: any) => {
    setEditingInvoice(invoice)
    setInvoiceDialogOpen(true)
  }

  // Handle saving edited invoice (matching sales.tsx)
  const handleSaveInvoice = async () => {
    if (!editingInvoice) return

    setInvoiceSaving(true)
    setInvoiceError(null)

    try {
      if (!invoiceForm.customerId) throw new Error('Customer is required')
      if (!invoiceForm.invoiceNumber) throw new Error('Invoice number is required')
      
      const payload = {
        companyId: selectedCompany,
        customerId: invoiceForm.customerId,
        invoiceNumber: invoiceForm.invoiceNumber,
        issueDate: invoiceForm.issueDate,
        dueDate: invoiceForm.dueDate || undefined,
        currency: invoiceForm.currency,
        lines: invoiceForm.lines.map((l: any) => ({ 
          description: l.description, 
          quantity: l.quantity, 
          unitPrice: l.unitPrice, 
          taxRate: l.taxRate, 
          lineDiscount: l.lineDiscount || 0, 
          productId: l.productId || null 
        })),
        // Backend model fields
        subtotal: invoiceForm.subtotal,
        taxAmount: invoiceForm.taxTotal,
        discountAmount: invoiceForm.discount,
        shippingAmount: invoiceForm.shipping,
        totalAmount: invoiceForm.totalAmount,
        balanceDue: invoiceForm.totalAmount,
        // Optional fields from form
        notes: invoiceForm.notes || '',
        terms: invoiceForm.terms || 'Payment due within 30 days',
        footer: '',
        paymentTerms: invoiceForm.paymentTerms || 'Net 30',
        lateFeeRate: null,
        deliveryMethod: 'email',
        taxInclusive: false,
        taxExemptionReason: null,
        createdBy: null,
        status: editingInvoice.status || 'draft', // Keep original status
        // Keep custom fields for potential future use
        fxRateSnapshot: 1.0,
        totalsSnapshot: {
          subtotal: invoiceForm.subtotal,
          taxTotal: invoiceForm.taxTotal,
          shipping: invoiceForm.shipping,
          discountMode: invoiceForm.discountMode,
          discount: invoiceForm.discount,
          total: invoiceForm.totalAmount
        }
      }

      const result = await apiService.updateInvoice(editingInvoice.id, payload as any)

      toast({
        title: 'Success',
        description: 'Invoice updated successfully',
        variant: 'default'
      })

      setInvoiceDialogOpen(false)
      setEditingInvoice(null)
      queryClient.invalidateQueries({ queryKey: ['pos-recent-invoices', selectedCompany] })
    } catch (error: any) {
      setInvoiceError(error.message || 'Failed to update invoice')
    } finally {
      setInvoiceSaving(false)
    }
  }

  // Handle soft delete invoice (matching sales.tsx pattern)
  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return

    setDeletingInvoice(true)
    try {
      await apiService.deleteInvoice(invoiceToDelete.id)
      
      toast({
        title: 'Success',
        description: 'Invoice deleted successfully',
        variant: 'default'
      })

      // Invalidate and refetch invoices
      queryClient.invalidateQueries({ queryKey: ['pos-recent-invoices', selectedCompany] })
      // Close the confirmation dialog
      setDeleteConfirmOpen(false)
      setInvoiceToDelete(null)
    } catch (error: any) {
      console.error('Error deleting invoice:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete invoice',
        variant: 'destructive'
      })
    } finally {
      setDeletingInvoice(false)
    }
  }

  const handleDownloadInvoicePdf = async (invoiceId: string, invoiceNumber: string) => {
    try {
      // Find the invoice data
      const invoice = recentInvoices.find(inv => inv.id === invoiceId)
      if (!invoice) {
        toast({
          title: "Invoice Not Found",
          description: "Could not find invoice data for PDF generation.",
          variant: "destructive"
        })
        return
      }

      // Find customer data
      const customer = customers.find(c => c.id === invoice.customerId)
      
      // Get company data with logo (same as journal PDF)
      let companyData = {
        name: 'Your Company',
        logoUrl: '',
        primaryColor: '#009688',
        website: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        email: '',
        phone: '',
        invoiceTerms: 'Payment is due within 30 days of invoice date.',
        invoiceFooter: 'Thank you for your business!'
      }
      
      try {
        const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
        if (companyId) {
          const company = await apiService.getCompany(companyId) as any
          if (company) {
            companyData.name = company.name || companyData.name
            let logoUrl = company.logoUrl || companyData.logoUrl
            
            // Convert relative URL to full URL for PDF generation
            if (logoUrl && logoUrl.startsWith('/api/')) {
              logoUrl = getApiUrl(logoUrl)
            }
            
            companyData.logoUrl = logoUrl
            companyData.primaryColor = company.primaryColor || companyData.primaryColor
            companyData.website = company.website || companyData.website
            companyData.address = company.address || companyData.address
            companyData.city = company.city || companyData.city
            companyData.state = company.state || companyData.state
            companyData.postalCode = company.postalCode || companyData.postalCode
            companyData.email = company.email || companyData.email
            companyData.phone = company.phone || companyData.phone
          }
        }
      } catch (error) {
        // Use fallback data if API fails
        companyData = {
          name: localStorage.getItem('company_name') || 'Your Company',
          logoUrl: localStorage.getItem('company_logoUrl') || '',
          primaryColor: localStorage.getItem('company_primaryColor') || '#009688',
          website: localStorage.getItem('company_website') || '',
          address: localStorage.getItem('company_address') || '',
          city: localStorage.getItem('company_city') || '',
          state: localStorage.getItem('company_state') || '',
          postalCode: localStorage.getItem('company_postalCode') || '',
          email: localStorage.getItem('company_email') || '',
          phone: localStorage.getItem('company_phone') || '',
          invoiceTerms: 'Payment is due within 30 days of invoice date.',
          invoiceFooter: 'Thank you for your business!'
        }
      }
      
      // Generate PDF using frontend generator
      const generator = new InvoicePDFGenerator({
        invoice: {
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          issueDate: invoice.issueDate,
          dueDate: invoice.dueDate,
          status: invoice.status,
          totalAmount: invoice.totalAmount,
          balanceDue: invoice.balanceDue,
          currency: customer?.currency || 'Frw',
          subtotal: (invoice as any).subtotal || invoice.totalAmount,
          taxAmount: (invoice as any).taxAmount || 0,
          discountAmount: (invoice as any).discountAmount || 0,
          customer: customer ? {
            name: customer.name,
            email: customer.email,
            address: customer.address,
            phone: (customer as any).phone,
            taxId: (customer as any).taxId
          } : undefined,
          lines: (invoice as any).lines || [],
          notes: (invoice as any).notes,
          paymentUrl: (invoice as any).paymentUrl
        },
        company: companyData
      })

      await generator.download()
      
      toast({
        title: "PDF Downloaded",
        description: `Invoice ${invoiceNumber} has been downloaded successfully.`,
      })
    } catch (error) {
      console.error('Error downloading invoice PDF:', error)
      
      // Fallback to backend API if frontend generator fails
      try {
        const blob = await apiService.getInvoicePdf(invoiceId)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `invoice-${invoiceNumber}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        toast({
          title: "PDF Downloaded",
          description: `Invoice ${invoiceNumber} has been downloaded successfully.`,
        })
      } catch (backendError) {
        console.error('Backend PDF generation also failed:', backendError)
        toast({
          title: "Download Failed",
          description: "There was an error downloading the PDF. Please try again.",
          variant: "destructive"
        })
      }
    }
  }

  const handleSendInvoiceEmail = async () => {
    if (!sendEmailInvoiceId || !sendEmailTo) return
    
    setSendEmailLoading(true)
    try {
      // Find the invoice data
      const invoice = recentInvoices.find(inv => inv.id === sendEmailInvoiceId)
      if (!invoice) {
        toast({
          title: "Invoice Not Found",
          description: "Could not find invoice data for email generation.",
          variant: "destructive"
        })
        return
      }

      // Find customer data
      const customer = customers.find(c => c.id === invoice.customerId)
      
      // Get company data with logo (same as journal PDF)
      let companyData = {
        name: 'Your Company',
        logoUrl: '',
        primaryColor: '#009688',
        website: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        email: '',
        phone: '',
        invoiceTerms: 'Payment is due within 30 days of invoice date.',
        invoiceFooter: 'Thank you for your business!'
      }
      
      try {
        const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
        if (companyId) {
          const company = await apiService.getCompany(companyId) as any
          if (company) {
            companyData.name = company.name || companyData.name
            let logoUrl = company.logoUrl || companyData.logoUrl
            
            // Convert relative URL to full URL for PDF generation
            if (logoUrl && logoUrl.startsWith('/api/')) {
              logoUrl = getApiUrl(logoUrl)
            }
            
            companyData.logoUrl = logoUrl
            companyData.primaryColor = company.primaryColor || companyData.primaryColor
            companyData.website = company.website || companyData.website
            companyData.address = company.address || companyData.address
            companyData.city = company.city || companyData.city
            companyData.state = company.state || companyData.state
            companyData.postalCode = company.postalCode || companyData.postalCode
            companyData.email = company.email || companyData.email
            companyData.phone = company.phone || companyData.phone
          }
        }
      } catch (error) {
        // Use fallback data if API fails
        companyData = {
          name: localStorage.getItem('company_name') || 'Your Company',
          logoUrl: localStorage.getItem('company_logoUrl') || '',
          primaryColor: localStorage.getItem('company_primaryColor') || '#009688',
          website: localStorage.getItem('company_website') || '',
          address: localStorage.getItem('company_address') || '',
          city: localStorage.getItem('company_city') || '',
          state: localStorage.getItem('company_state') || '',
          postalCode: localStorage.getItem('company_postalCode') || '',
          email: localStorage.getItem('company_email') || '',
          phone: localStorage.getItem('company_phone') || '',
          invoiceTerms: 'Payment is due within 30 days of invoice date.',
          invoiceFooter: 'Thank you for your business!'
        }
      }
      
      // Generate PDF using frontend generator
      const generator = new InvoicePDFGenerator({
        invoice: {
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          issueDate: invoice.issueDate,
          dueDate: invoice.dueDate,
          status: invoice.status,
          totalAmount: invoice.totalAmount,
          balanceDue: invoice.balanceDue,
          currency: customer?.currency || 'Frw',
          subtotal: (invoice as any).subtotal || invoice.totalAmount,
          taxAmount: (invoice as any).taxAmount || 0,
          discountAmount: (invoice as any).discountAmount || 0,
          customer: customer ? {
            name: customer.name,
            email: customer.email,
            address: customer.address,
            phone: (customer as any).phone,
            taxId: (customer as any).taxId
          } : undefined,
          lines: (invoice as any).lines || [],
          notes: (invoice as any).notes,
          paymentUrl: (invoice as any).paymentUrl
        },
        company: companyData
      })

      // Generate PDF blob
      const pdfBlob = await generator.generate()
      // Debug logging removed
      
      // Debug logging removed;
      
      // Send email with frontend-generated PDF
      await apiService.sendInvoiceEmail(invoice.id, {
        to: sendEmailTo,
        subject: `Invoice ${invoice.invoiceNumber}`,
        message: `Please find your invoice ${invoice.invoiceNumber} attached. Thank you for your business!`,
        attachPdf: true,
        pdfBlob: pdfBlob
      })
      
      toast({
        title: "Email Sent",
        description: `Invoice ${invoice.invoiceNumber} has been sent to ${sendEmailTo}`,
      })
      
      setSendEmailOpen(false)
      setSendEmailTo('')
      setSendEmailInvoiceId(null)
    } catch (error) {
      console.error('Error sending invoice email:', error)
      
      // Fallback to backend API if frontend generation fails
      try {
        const result = await apiService.sendInvoiceEmail(sendEmailInvoiceId, { 
          to: sendEmailTo, 
          attachPdf: true 
        })
        
        // Check if there's a warning about SMTP not being configured
        if (result?.warning) {
          toast({
            title: "Email Queued",
            description: result.warning,
            variant: "default"
          })
        } else {
          toast({
            title: "Email Sent",
            description: `Invoice sent to ${sendEmailTo}`,
          })
        }
        
        setSendEmailOpen(false)
        setSendEmailTo('')
        setSendEmailInvoiceId(null)
      } catch (backendError: any) {
        console.error('Backend email sending failed:', backendError)
        
        toast({
          title: "Email Service Unavailable",
          description: "Email service is not configured. Please contact your administrator to set up SMTP settings.",
          variant: "destructive"
        })
      }
    } finally {
      setSendEmailLoading(false)
    }
  }

  const handleCreatePaymentLink = async (invoiceId: string, invoiceNumber: string) => {
    try {
      const response = await apiService.createPaymentLink(invoiceId)
      
      // Copy link to clipboard
      await navigator.clipboard.writeText(response.url)
      
      toast({
        title: "Payment Link Created",
        description: `Link for ${invoiceNumber} copied to clipboard`,
      })
    } catch (error) {
      toast({
        title: "Link Creation Failed",
        description: "Unable to create payment link",
        variant: "destructive"
      })
    }
  }

  // Fetch bank accounts for payment processing
  const fetchBankAccounts = async () => {
    if (!selectedCompany) return

    setLoadingBankAccounts(true)
    try {
      const accounts = await bankingApi.getBankAccounts(selectedCompany)
      setBankAccounts(accounts || [])

      // Auto-select first account if none selected
      if (accounts && accounts.length > 0 && !selectedBankAccount) {
        setSelectedBankAccount(accounts[0].id)
      }
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error)
      toast({
        title: "Bank Accounts Error",
        description: "Unable to load bank accounts",
        variant: "destructive"
      })
    } finally {
      setLoadingBankAccounts(false)
    }
  }

  // Filter customer banks based on search term
  const filterCustomerBanks = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredCustomerBanks(CUSTOMER_PAYMENT_BANKS)
      return
    }
    
    const filtered = CUSTOMER_PAYMENT_BANKS.filter(bank =>
      bank.toLowerCase().includes(searchTerm.toLowerCase())
    )
    setFilteredCustomerBanks(filtered)
  }

  // Handle customer bank search
  const handleCustomerBankSearch = (value: string) => {
    setCustomerBankSearchTerm(value)
    filterCustomerBanks(value)
  }

  // Helper function to get stock display for services vs products
  const getStockDisplay = (product: POSProduct) => {
    // Convert string values to numbers and handle the case where availableQuantity is 0 but stockQuantity has value
    const availableQty = Number(product.availableQuantity) || 0
    const stockQty = Number(product.stockQuantity) || 0
    
    // Use availableQuantity if it's > 0, otherwise fall back to stockQuantity
    const availableStock = availableQty > 0 ? availableQty : stockQty
    
    if (isService(product)) {
      // Services handle "stock" differently
      if (availableStock >= 999999) {
        const result = { text: tSync("Available"), status: "available" }
        return result
      } else if (availableStock > 50) {
        const result = { text: tSync(`${availableStock} slots available`), status: "available" }
        return result
      } else if (availableStock > 10) {
        const result = { text: tSync(`${availableStock} slots available`), status: "limited" }
        return result
      } else if (availableStock > 0) {
        const result = { text: tSync(`Only ${availableStock} slots left`), status: "low" }
        return result
      } else {
        const result = { text: tSync("Fully Booked"), status: "unavailable" }
        return result
      }
    } else {
      // Physical products
      if (availableStock <= 0) {
        const result = { text: tSync("Out of Stock"), status: "unavailable" }
        return result
      } else if (availableStock <= 10) {
        const result = { text: `${tSync("Stock")}: ${availableStock}`, status: "low" }
        return result
      } else {
        const result = { text: `${tSync("Stock")}: ${availableStock}`, status: "available" }
        return result
      }
    }
  }

  // Helper function to get appropriate icon for product type
  const getProductIcon = (product: POSProduct) => {
    if (isService(product)) {
      // Return service-specific icons based on category or service type
      const categoryName = product.categoryObject?.name?.toLowerCase() || ''
      if (categoryName.includes('consulting')) return '💼'
      if (categoryName.includes('technical')) return '💻'
      if (categoryName.includes('creative')) return '🎨'
      if (categoryName.includes('maintenance')) return '🔧'
      if (categoryName.includes('training')) return '🎓'
      return '⚡' // Default service icon
    }
    return <Package className="w-8 h-8 text-blue-600" />
  }

  // Handle customer selection with recent customers tracking
  const handleCustomerSelection = (customer: POSCustomer) => {
    setSelectedCustomer(customer)
    setShowCustomerDialog(false)
    setCustomerSearch("")
    
    // Add to recent customers
    setRecentCustomerIds(prev => {
      const filtered = prev.filter(id => id !== customer.id)
      return [customer.id, ...filtered].slice(0, 5)
    })
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when not in input fields
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      // Customer selection shortcut (Ctrl/Cmd + C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault()
        setShowCustomerDialog(true)
      }
      
      // Clear cart shortcut (Ctrl/Cmd + Delete)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Delete') {
        e.preventDefault()
        clearCart()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Show loading state while authentication is being initialized
  if (authLoading || !isAuthenticated) {
    return (
      <div className="bg-slate-50 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading POS System</h2>
          <p className="text-slate-600">Initializing point of sale...</p>
        </div>
      </div>
    )
  }

  return (
    <div 
      className="bg-slate-50 flex flex-col overflow-hidden h-full"
      style={{
        height: 'calc(100vh - 3.5rem)',
        maxHeight: 'calc(100vh - 3.5rem)',
        minHeight: 'calc(100vh - 3.5rem)',
        paddingTop: '1rem',
      }}
    >
      {/* Header - Fixed Height */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
            <ShoppingCart className="w-3 h-3 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900"><TranslatedText text="Point of Sale" /></h1>
            <p className="text-xs text-slate-600"><TranslatedText text="Quick checkout system" /></p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="h-6 text-xs border-slate-300 hover:bg-slate-50 hover:border-slate-400 hover:text-slate-700 transition-all duration-200"
            onClick={exportPOSData}
          >
            <Download className="w-3 h-3 mr-1" />
            <TranslatedText text="Export Data" />
          </Button>
          <Badge variant="secondary" className="h-6 text-xs">
            <Clock className="w-3 h-3 mr-1" />
            {new Date().toLocaleTimeString()}
          </Badge>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowCustomerDialog(true)}
            className="h-6 text-xs"
            title={tSync("Select Customer (Ctrl+C)")}
          >
            <User className="w-3 h-3 mr-1" />
            <TranslatedText text="Customer" />
          </Button>
          <Button variant="outline" size="sm" onClick={clearCart} disabled={cart.length === 0} className="h-6 text-xs" title={tSync("Clear Cart (Ctrl+Delete)")}>
            <Trash2 className="w-3 h-3 mr-1" />
            <TranslatedText text="Clear" />
          </Button>
        </div>
      </div>

      <div className="flex-1 flex min-h-0 max-h-full">
        {/* Left Panel - Products */}
        <div className="flex-1 flex flex-col min-h-0 max-h-full">
          {/* Controls Bar - Fixed */}
          <div className="bg-white px-6 py-3 border-b border-slate-200 flex-shrink-0">
            {/* Search & Barcode Scanner */}
            <div className="mb-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder={searchPlaceholder}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-9 h-8 text-sm"
                  />
                </div>
                {/* Barcode Scanner Button - Hidden */}
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={startBarcodeScanning}
                  disabled={isScanning}
                  className="h-8 px-3"
                  title="Barcode Scanner (Ctrl+B)"
                >
                  <Scan className="w-4 h-4" />
                </Button> */}
              </div>
            </div>

            {/* Favorite Products Quick Add */}
            {favoriteProductsList.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-slate-700"><TranslatedText text="Quick Add" /></span>
                </div>
                <div className="flex gap-1 overflow-x-auto pb-1">
                  {favoriteProductsList.map(product => (
                    <Button
                      key={product.id}
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        addToCart(product)
                      }}
                      className="h-6 text-xs whitespace-nowrap flex-shrink-0"
                    >
                      {product.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Category Filters */}
            {categories.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Filter className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-slate-700"><TranslatedText text="Categories" /></span>
                </div>
                <div className="flex gap-1 overflow-x-auto pb-1">
                  <Button
                    size="sm"
                    variant={!selectedCategory ? "default" : "outline"}
                    onClick={() => setSelectedCategory("")}
                    className="h-6 text-xs whitespace-nowrap flex-shrink-0"
                  >
                    <TranslatedText text="All" />
                  </Button>
                  {categories.map(category => (
                    <Button
                      key={category.id}
                      size="sm"
                      variant={selectedCategory === category.id ? "default" : "outline"}
                      onClick={() => setSelectedCategory(category.id)}
                      className="h-6 text-xs whitespace-nowrap flex-shrink-0"
                    >
                      {category.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Available Products Count */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-xs h-5">
                  {filteredProducts.length} <TranslatedText text="available for sale" />
                </Badge>
                <div className="text-xs text-slate-500">
                  <TranslatedText text="Only showing active products with stock" />
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid - Scrollable */}
          <div className="flex-1 px-6 py-3 overflow-y-auto bg-slate-50 min-h-0">
            {productsLoading ? (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-2">
                {[...Array(14)].map((_, i) => (
                  <div key={i} className="aspect-square bg-slate-200 rounded-md animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-2 pb-2">
                {filteredProducts.map((product: POSProduct) => {
                  const stockDisplay = getStockDisplay(product)
                  const isLowOrLimited = stockDisplay.status === 'low' || stockDisplay.status === 'limited'
                  const isOutOfStock = stockDisplay.status === 'unavailable'
                  const productIcon = getProductIcon(product)
                  
                  return (
                    <Card
                      key={product.id}
                      className={`cursor-pointer hover:shadow-md transition-all duration-150 hover:scale-[1.02] group relative ${
                        isOutOfStock ? 'opacity-60 border-red-200 cursor-not-allowed' : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        !isOutOfStock && addToCart(product)
                      }}
                    >
                      <CardContent className="p-2 aspect-square flex flex-col">
                        <div className="flex-1 flex items-center justify-center mb-1">
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center group-hover:scale-105 transition-all duration-150 ${
                            isService(product) 
                              ? 'bg-gradient-to-br from-purple-100 to-purple-200' 
                              : 'bg-gradient-to-br from-blue-100 to-blue-200'
                          }`}>
                            {typeof productIcon === 'string' ? (
                              <span className="text-sm">{productIcon}</span>
                            ) : (
                              <div className="w-4 h-4 text-blue-600">
                                <Package className="w-full h-full" />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-center">
                          <h3 className="font-medium text-slate-900 text-xs leading-tight mb-0.5 line-clamp-1">
                            {product.name}
                          </h3>
                          {product.sku && (
                            <p className="text-xs text-slate-500 mb-1 line-clamp-1">
                              {product.sku}
                            </p>
                          )}
                          {isService(product) && (
                            <div className="text-xs bg-purple-50 text-purple-700 rounded px-1 mb-1">
                              <TranslatedText text="Service" />
                            </div>
                          )}
                          <div className={`text-xs font-bold ${isService(product) ? 'text-purple-600' : 'text-green-600'}`}>
                            {formatCurrency((product.unitPrice && product.unitPrice > 0) ? product.unitPrice : (product.costPrice && product.costPrice > 0 ? product.costPrice : 0), 'Frw')}
                          </div>
                          <div className={`text-xs ${
                            stockDisplay.status === 'unavailable' ? 'text-red-600' : 
                            stockDisplay.status === 'low' || stockDisplay.status === 'limited' ? 'text-orange-600' : 
                            'text-slate-500'
                          }`}>
                            {stockDisplay.text}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="absolute top-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              setShowProductInfo(product)
                            }}
                            className="h-5 w-5 p-0 bg-white/80 hover:bg-white"
                          >
                            <Info className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFavorite(product.id)
                            }}
                            className={`h-5 w-5 p-0 ${
                              favoriteProducts.includes(product.id) 
                                ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
                                : 'bg-white/80 hover:bg-white'
                            }`}
                          >
                            <Star className="w-3 h-3" fill={favoriteProducts.includes(product.id) ? "currentColor" : "none"} />
                          </Button>
                        </div>

                        {/* Compact Status Badge */}
                        {stockDisplay.status === 'unavailable' && (
                          <div className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                        )}
                        {/* Stock Level Indicators */}
                        {stockDisplay.status === 'low' && (
                          <div className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></div>
                        )}
                        {stockDisplay.status === 'limited' && isService(product) && (
                          <div className="absolute top-1 right-1 w-2 h-2 bg-yellow-500 rounded-full"></div>
                        )}
                        
                       
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            )}
            
            {!productsLoading && filteredProducts.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                <Package className="w-12 h-12 mb-3 text-slate-300" />
                <h3 className="font-medium mb-1"><TranslatedText text="No products found" /></h3>
                <p className="text-sm"><TranslatedText text="Try adjusting your search" /></p>
              </div>
            )}
          </div>

          {/* Products Summary - Fixed at Bottom */}
          <div className="bg-white border-t border-slate-200 px-6 py-3 flex-shrink-0">
            <div className="grid grid-cols-4 gap-3 text-center">
              {/* Total Products */}
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="text-lg font-bold text-slate-900">
                  {filteredProducts.length}
                </div>
                <div className="text-xs text-slate-600"><TranslatedText text="Products" /></div>
              </div>
              
              {/* Cart Items */}
              <div className="bg-blue-50 rounded-lg p-2">
                <div className="text-lg font-bold text-blue-700">
                  {cart.length}
                </div>
                <div className="text-xs text-blue-600"><TranslatedText text="In Cart" /></div>
              </div>
              
              {/* Cart Total */}
              <div className="bg-green-50 rounded-lg p-2">
                <div className="text-lg font-bold text-green-700">
                  {formatCurrency(total)}
                </div>
                <div className="text-xs text-green-600"><TranslatedText text="Total" /></div>
              </div>
              
              {/* Quick Actions */}
              <div className="flex flex-col gap-1">
                {/* Barcode Scanner Button - Hidden */}
                {/* <Button
                  size="sm"
                  variant="outline"
                  onClick={startBarcodeScanning}
                  disabled={isScanning}
                  className="h-6 text-xs"
                  title="Barcode Scanner (Ctrl+B)"
                >
                  <Scan className="w-3 h-3" />
                </Button> */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCustomerDialog(true)}
                  className="h-6 text-xs"
                  title={tSync("Select Customer")}
                >
                  <User className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Cart & Checkout */}
        <div className="w-72 bg-white border-l border-slate-200 flex flex-col min-h-0 max-h-full">
          {/* Enhanced Customer Selection - Fixed */}
          <div className="p-2 border-b border-slate-200 flex-shrink-0">
            <Button
              variant={selectedCustomer ? "default" : "outline"}
              onClick={() => setShowCustomerDialog(true)}
              className="w-full h-auto justify-start text-xs p-2"
            >
              <div className="flex items-center gap-2 w-full">
                {selectedCustomer ? (
                  <>
                    {/* Customer Avatar */}
                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {selectedCustomer.name.charAt(0).toUpperCase()}
                    </div>
                    {/* Customer Info */}
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium truncate">{selectedCustomer.name}</div>
                      {(selectedCustomer.email || selectedCustomer.phone) && (
                        <div className="text-xs opacity-75 truncate">
                          {selectedCustomer.email || selectedCustomer.phone}
                        </div>
                      )}
                    </div>
                    {/* Clear Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedCustomer(null)
                      }}
                      className="h-4 w-4 p-0 hover:bg-white/20"
                    >
                      <X className="w-2 h-2" />
                    </Button>
                  </>
                ) : (
                  <>
                    <User className="w-3 h-3" />
                    <span className="truncate"><TranslatedText text="Select Customer" /></span>
                  </>
                )}
              </div>
            </Button>
          </div>

          {/* Recent Invoices Section - Collapsible */}
          <div className="border-b border-slate-200 flex-shrink-0">
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRecentInvoices(!showRecentInvoices)}
                className="w-full justify-between h-8 text-xs"
              >
                <div className="flex items-center gap-2">
                  <History className="w-3 h-3" />
                  <span><TranslatedText text="Recent Invoices" /></span>
                  {!invoicesLoading && recentInvoices.length > 0 && (
                    <Badge variant="secondary" className="h-4 text-xs">
                      {recentInvoices.length}
                    </Badge>
                  )}
                </div>
                {showRecentInvoices ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </Button>
              
              {showRecentInvoices && (
                <div className="mt-2 space-y-1">
                  {invoicesLoading ? (
                    <div className="space-y-1">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
                      ))}
                    </div>
                  ) : recentInvoices.length === 0 ? (
                    <div className="text-center py-3 text-slate-500">
                      <Receipt className="w-4 h-4 mx-auto mb-1 text-slate-300" />
                      <p className="text-xs"><TranslatedText text="No recent invoices" /></p>
                    </div>
                  ) : (
                    recentInvoices.map((invoice) => {
                      const customer = customers.find(c => c.id === invoice.customerId)
                      const isOverdue = invoice.dueDate && new Date(invoice.dueDate) < new Date() && invoice.balanceDue > 0
                      const showActions = selectedInvoiceActions === invoice.id
                      
                      return (
                        <div
                          key={invoice.id}
                          className="bg-slate-50 rounded-lg p-2 border border-slate-200 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1 mb-1">
                                <span className="text-xs font-medium text-slate-900">
                                  {invoice.invoiceNumber}
                                </span>
                                <Badge 
                                  variant={
                                    invoice.status === "paid" ? "default" :
                                    isOverdue ? "destructive" : 
                                    "secondary"
                                  }
                                  className="text-xs h-4"
                                >
                                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                </Badge>
                                {isOverdue && (
                                  <Badge variant="destructive" className="text-xs h-4">
                                    <TranslatedText text="Overdue" />
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-600 truncate">
                                {customer ? customer.name : <TranslatedText text="Unknown Customer" />}
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(invoice.issueDate).toLocaleDateString()}
                                {invoice.dueDate && (
                                  <span className="ml-2">
                                    <TranslatedText text="Due" />: {new Date(invoice.dueDate).toLocaleDateString()}
                                  </span>
                                )}
                              </p>
                            </div>
                            <div className="text-right ml-2">
                              {(() => {
                                const customer = allCustomers.find(c => c.id === invoice.customerId)
                                const currency = customer?.currency || invoice.currency || 'Frw'
                                const displayCurrency = currency === 'USD' ? 'Frw' : currency
                                const converted = convertedInvoiceAmounts[invoice.id]
                                const totalAmountToDisplay = currency === 'USD' && converted ? converted.totalAmount : invoice.totalAmount
                                const balanceDueToDisplay = currency === 'USD' && converted ? converted.balanceDue : invoice.balanceDue
                                
                                return (
                                  <>
                                    <p className="text-xs font-semibold text-slate-900">
                                      {formatCurrency(totalAmountToDisplay, displayCurrency)}
                                    </p>
                                    {invoice.balanceDue > 0 && (
                                      <p className="text-xs text-amber-600">
                                        Due: {formatCurrency(balanceDueToDisplay, displayCurrency)}
                                      </p>
                                    )}
                                  </>
                                )
                              })()}
                            </div>
                          </div>
                          
                          {/* Action Toggle Button */}
                          <div className="flex gap-1 mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs flex-1"
                              onClick={() => {
                                setSelectedInvoiceActions(showActions ? null : invoice.id)
                              }}
                            >
                              {showActions ? (
                                <>
                                  <ChevronUp className="w-3 h-3 mr-1" />
                                  <TranslatedText text="Hide" />
                                </>
                              ) : (
                                <>
                                  <Eye className="w-3 h-3 mr-1" />
                                  <TranslatedText text="Actions" />
                                </>
                              )}
                            </Button>
                            
                            {/* Quick Customer Select */}
                            {invoice.status !== 'paid' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  selectCustomerFromInvoice(invoice)
                                }}
                              >
                                <ShoppingCart className="w-3 h-3 mr-1" />
                                <TranslatedText text="Select" />
                              </Button>
                            )}
                          </div>

                          {/* Expanded Actions Panel */}
                          {showActions && (
                            <div className="mt-2 p-2 bg-white rounded border border-slate-200 space-y-2">
                              <div className="grid grid-cols-2 gap-1">
                                {/* View Invoice Details */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => handleViewInvoice(invoice)}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  <TranslatedText text="View" />
                                </Button>

                                {/* Download PDF */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => handleDownloadInvoicePdf(invoice.id, invoice.invoiceNumber)}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  <TranslatedText text="PDF" />
                                </Button>

                                {/* Send Email */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    setSendEmailInvoiceId(invoice.id)
                                    setSendEmailTo(customer?.email || '')
                                    setSendEmailOpen(true)
                                  }}
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  <TranslatedText text="Email" />
                                </Button>

                                {/* Payment Link - Hidden per user request */}
                                {/* {invoice.balanceDue > 0 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => handleCreatePaymentLink(invoice.id, invoice.invoiceNumber)}
                                  >
                                    <Copy className="w-3 h-3 mr-1" />
                                    <TranslatedText text="Link" />
                                  </Button>
                                )} */}
                              </div>

                              {/* Status Actions */}
                              {invoice.status === 'draft' && (
                                <div className="space-y-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full h-7 text-xs"
                                    onClick={() => handleMarkInvoiceAsSent(invoice.id)}
                                    disabled={markingAsSent === invoice.id}
                                  >
                                    {markingAsSent === invoice.id ? (
                                      <>
                                        <div className="w-3 h-3 mr-1 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                                        <TranslatedText text="Sending..." />
                                      </>
                                    ) : (
                                      <>
                                        <Send className="w-3 h-3 mr-1" />
                                        <TranslatedText text="Mark as Sent" />
                                      </>
                                    )}
                                  </Button>
                                  
                                  {/* Edit and Delete Actions for Draft Invoices */}
                                  <div className="grid grid-cols-2 gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() => handleEditInvoice(invoice)}
                                    >
                                      <Edit className="w-3 h-3 mr-1" />
                                      <TranslatedText text="Edit" />
                                    </Button>
                                    
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => {
                                        setInvoiceToDelete(invoice)
                                        setDeleteConfirmOpen(true)
                                      }}
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" />
                                      <TranslatedText text="Delete" />
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {(invoice.status === 'sent' || invoice.status === 'pending') && (
                                <div className="space-y-1">
                                  <Button
                                    variant="default"
                                    size="sm"
                                    className="w-full h-7 text-xs bg-green-600 hover:bg-green-700"
                                    onClick={() => handleMarkInvoiceAsPaid(invoice.id)}
                                    disabled={markingAsPaid === invoice.id}
                                  >
                                    {markingAsPaid === invoice.id ? (
                                      <>
                                        <div className="w-3 h-3 mr-1 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                        <TranslatedText text="Processing..." />
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        <TranslatedText text="Mark as Paid" />
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Cart Header - Fixed */}
          <div className="px-2 py-1 border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-sm text-slate-900"><TranslatedText text="Cart" /></h3>
              <Badge variant="secondary" className="text-xs h-4">{cart.length}</Badge>
            </div>
          </div>

          {/* Cart Items - Scrollable */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-2 pb-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900"><TranslatedText text="Cart Items" /></h3>
                <Badge variant="secondary">{cart.length} <TranslatedText text="items" /></Badge>
              </div>
            </div>

            <div className="px-2 pb-2">
              {cart.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <ShoppingCart className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                  <p className="text-xs font-medium"><TranslatedText text="Cart is empty" /></p>
                  <p className="text-xs"><TranslatedText text="Add items to start" /></p>
                </div>
              ) : (
                <div className="space-y-1">
                  {cart.map((item) => (
                  <div
                    key={item.product.id}
                    className="bg-slate-50 rounded-md p-1.5 border border-slate-200"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-xs text-slate-900 truncate">
                          {item.product.name}
                        </h4>
                        <p className="text-xs text-slate-500">
                          {(() => {
                            const currency = (selectedCustomer as any)?.currency || 'Frw'
                            const displayCurrency = currency === 'USD' ? 'Frw' : currency
                            // Convert price if customer currency is USD
                            let priceToDisplay = item.unitPrice
                            if (currency === 'USD' && item.product?.id && convertedProductPrices[item.product.id]) {
                              priceToDisplay = convertedProductPrices[item.product.id]
                            } else if (currency === 'USD') {
                              // Convert on-the-fly if not cached
                              // For now, use original price (conversion will happen in background)
                              priceToDisplay = item.unitPrice
                            }
                            return formatCurrency(priceToDisplay, displayCurrency)
                          })()} <TranslatedText text="each" />
                          {item.product.taxInclusive && item.product.taxRate && item.product.taxRate > 0 && (
                            <span className="ml-1 text-xs text-green-600 font-medium">
                              <TranslatedText text="(tax incl.)" />
                            </span>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromCart(item.product.id)}
                        className="h-5 w-5 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 ml-1"
                      >
                        <X className="w-2 h-2" />
                      </Button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="h-5 w-5 p-0"
                        >
                          <Minus className="w-2 h-2" />
                        </Button>
                        <span className="w-5 text-center text-xs font-medium">
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="h-5 w-5 p-0"
                        >
                          <Plus className="w-2 h-2" />
                        </Button>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-slate-900 text-xs">
                          {formatCurrency(item.lineTotal)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}
            </div>
          </div>

          {/* Cart Summary - Always Fixed at Bottom */}
          <div className="border-t border-slate-200 p-2 space-y-2 flex-shrink-0 bg-white">
            {cart.length > 0 ? (
              <>
                {/* Quick Actions */}
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDiscountDialog(true)}
                    className="flex-1 h-6 text-xs"
                  >
                    <Percent className="w-3 h-3 mr-1" />
                    <TranslatedText text="Discount" />
                  </Button>
                </div>

                {/* Totals */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600"><TranslatedText text="Subtotal" /></span>
                    <span className="font-medium">{formatCurrency(subtotal)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-600"><TranslatedText text="Discount" /></span>
                      <span className="font-medium text-red-600">-{formatCurrency(discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600"><TranslatedText text="Tax" /></span>
                    <span className="font-medium">{formatCurrency(taxAmount)}</span>
                  </div>
                  <div className="flex justify-between text-xs font-bold border-t border-slate-200 pt-1">
                    <span><TranslatedText text="Total" /></span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>

                {/* Checkout Button */}
                <Button
                  onClick={async () => {
                    setShowPaymentDialog(true)
                    await fetchBankAccounts()
                  }}
                  disabled={!selectedCustomer || cart.length === 0}
                  className="w-full h-8 text-xs font-medium bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                >
                  <CreditCard className="w-3 h-3 mr-1" />
                  <TranslatedText text="Checkout" /> {formatCurrency(total)}
                </Button>
              </>
            ) : (
              /* Empty Cart State in Summary */
              <div className="text-center py-2 text-slate-400">
                <div className="text-xs"><TranslatedText text="Cart Summary" /></div>
                <div className="text-xs mt-1"><TranslatedText text="Add items to see totals" /></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Customer Selection Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span><TranslatedText text="Select Customer" /></span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingCustomer(null)
                  setCustomerForm({ name: "", email: "", phone: "", tin: "", currency: 'Frw' })
                  setCustomerDialogOpen(true)
                }}
                className="h-7 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                <TranslatedText text="New Customer" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col space-y-3 flex-1 overflow-hidden">
            {/* Enhanced Search */}
            <div className="relative flex-shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder={tSync("Search by name, email, or phone...")}
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="pl-9 h-10"
                autoFocus
              />
              {customerSearch && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCustomerSearch("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCustomerSearch("")}
                className="h-7 text-xs"
              >
                <Users className="w-3 h-3 mr-1" />
                <TranslatedText text="All Customers" />
              </Button>
              {recentCustomers.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Show only recent customers
                    const recentNames = recentCustomers.map(c => c.name).join(' ')
                    setCustomerSearch(recentNames)
                  }}
                  className="h-7 text-xs"
                >
                  <Clock className="w-3 h-3 mr-1" />
                  <TranslatedText text="Recent" /> ({recentCustomers.length})
                </Button>
              )}
            </div>
            
            {/* Customer List */}
            <div className="flex-1 overflow-y-auto space-y-1">
              {customersLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-12 bg-slate-100 rounded-md animate-pulse" />
                  ))}
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-medium"><TranslatedText text="No customers found" /></p>
                  <p className="text-xs"><TranslatedText text="Try a different search term" /></p>
                </div>
              ) : (
                filteredCustomers.map((customer: POSCustomer) => (
                  <Button
                    key={customer.id}
                    variant="ghost"
                    onClick={() => handleCustomerSelection(customer)}
                    className="w-full justify-start h-auto p-3 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3 w-full">
                      {/* Customer Avatar */}
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                        {customer.name.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* Customer Info */}
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-medium text-sm text-slate-900 truncate">
                          {customer.name}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {customer.email && (
                            <span className="truncate">{customer.email}</span>
                          )}
                          {customer.phone && (
                            <span className="truncate">{customer.phone}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Selection Indicator */}
                      {selectedCustomer?.id === customer.id && (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      )}
                    </div>
                  </Button>
                ))
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex gap-2 pt-2 border-t border-slate-200 flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => setShowCustomerDialog(false)}
                className="flex-1 h-8 text-xs"
              >
                <TranslatedText text="Cancel" />
              </Button>
              {selectedCustomer && (
                <Button
                  onClick={() => {
                    setSelectedCustomer(null)
                    setShowCustomerDialog(false)
                  }}
                  className="flex-1 h-8 text-xs"
                >
                  <TranslatedText text="Clear Selection" />
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Creation Dialog */}
      <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? <TranslatedText text="Edit Customer" /> : <TranslatedText text="Add Customer" />}</DialogTitle>
            <DialogDescription><TranslatedText text="Enter customer details below." /></DialogDescription>
          </DialogHeader>
          {customerError && (
            <div className="text-red-600 text-sm">{customerError}</div>
          )}
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="cust-name"><TranslatedText text="Name" /> <span className="text-red-500">*</span></Label>
              <Input 
                id="cust-name" 
                value={customerForm.name} 
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} 
                required 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-phone"><TranslatedText text="Phone" /> <span className="text-red-500">*</span></Label>
              <Input 
                id="cust-phone" 
                type="tel" 
                value={customerForm.phone || ''} 
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} 
                placeholder={tSync("e.g., +1234567890")}
                required 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-tin"><TranslatedText text="TIN (Tax Identification Number)" /> <span className="text-red-500">*</span></Label>
              <Input 
                id="cust-tin" 
                value={customerForm.tin || ''} 
                onChange={(e) => setCustomerForm({ ...customerForm, tin: e.target.value })} 
                placeholder={tSync("e.g., 123456789")}
                required 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-email"><TranslatedText text="Email (Optional)" /></Label>
              <Input 
                id="cust-email" 
                type="email" 
                value={customerForm.email || ''} 
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} 
                placeholder={tSync("customer@example.com")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-currency"><TranslatedText text="Currency" /></Label>
              <select 
                id="cust-currency" 
                className="w-full border rounded px-2 py-2" 
                value={customerForm.currency || 'Frw'} 
                onChange={(e) => setCustomerForm({ ...customerForm, currency: e.target.value })}
              >
                {['Frw','USD','EUR','GBP','KES','NGN'].map(c => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerDialogOpen(false)} disabled={customerSaving}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button onClick={async () => {
              try {
                setCustomerSaving(true)
                setCustomerError(null)
                if (!customerForm.name || customerForm.name.trim() === '') {
                  throw new Error(tSync('Name is required'))
                }
                if (!customerForm.phone || customerForm.phone.trim() === '') {
                  throw new Error(tSync('Phone is required'))
                }
                if (!customerForm.tin || customerForm.tin.trim() === '') {
                  throw new Error(tSync('TIN (Tax Identification Number) is required'))
                }
                if (!selectedCompany) throw new Error(tSync('No company selected'))
                if (editingCustomer) {
                  await apiService.updateCustomer(editingCustomer.id, { 
                    companyId: selectedCompany, 
                    name: customerForm.name, 
                    email: customerForm.email, 
                    phone: customerForm.phone,
                    taxNumber: customerForm.tin,
                    currency: customerForm.currency 
                  })
                  toast({ title: 'Customer updated', description: `${customerForm.name}` })
                } else {
                  const newCustomer = await apiService.createCustomer({ 
                    companyId: selectedCompany, 
                    name: customerForm.name, 
                    email: customerForm.email, 
                    phone: customerForm.phone,
                    taxNumber: customerForm.tin,
                    currency: customerForm.currency 
                  })
                  toast({ title: 'Customer created', description: `${customerForm.name}` })
                  
                  // Optimistically update the customer list
                  queryClient.setQueryData(['pos-customers', selectedCompany], (oldData: any) => {
                    if (!oldData) {
                      return { items: [newCustomer], total: 1 }
                    }
                    return {
                      ...oldData,
                      items: [...(oldData.items || []), newCustomer],
                      total: (oldData.total || oldData.items?.length || 0) + 1
                    }
                  })
                  
                  // Force refetch to ensure data is fresh
                  await queryClient.invalidateQueries({ queryKey: ["pos-customers", selectedCompany] })
                  await queryClient.invalidateQueries({ queryKey: ["customers", selectedCompany] })
                  
                  // Refetch customers immediately
                  await refetchCustomers()
                  
                  // Auto-select the newly created customer
                  setSelectedCustomer({
                    id: newCustomer.id,
                    name: newCustomer.name,
                    email: newCustomer.email,
                    phone: newCustomer.phone
                  })
                  
                  // Clear customer search so the new customer is visible in the list
                  setCustomerSearch("")
                  
                  // Close customer creation dialog but keep selection dialog open
                  // so user can see the customer was added and selected
                }
                setCustomerDialogOpen(false)
              } catch (e: any) {
                setCustomerError((e as any)?.message || 'Failed to save customer')
                toast({ 
                  title: 'Customer save failed', 
                  description: (e as any)?.message || 'Failed to save customer', 
                  variant: 'destructive' 
                })
              } finally {
                setCustomerSaving(false)
              }
            }} disabled={customerSaving}>
              {customerSaving ? 'Saving…' : (editingCustomer ? 'Save Changes' : 'Create Customer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg"><TranslatedText text="Process Payment" /></DialogTitle>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(total)}</p>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Payment Methods */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant={paymentMethod === 'card' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('card')}
                className="h-12 flex-col"
              >
                <CreditCard className="w-5 h-5 mb-1" />
                <span className="text-xs"><TranslatedText text="Card" /></span>
              </Button>
              <Button
                variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('cash')}
                className="h-12 flex-col"
              >
                <Banknote className="w-5 h-5 mb-1" />
                <span className="text-xs"><TranslatedText text="Cash" /></span>
              </Button>
              <Button
                variant={paymentMethod === 'mobile' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('mobile')}
                className="h-12 flex-col"
              >
                <Smartphone className="w-5 h-5 mb-1" />
                <span className="text-xs"><TranslatedText text="Mobile" /></span>
              </Button>
            </div>

            {/* Bank Account Selection - Company's Bank (Where payment will be received) */}
            {paymentMethod !== 'cash' && (
              <div className="space-y-2">
                <Label htmlFor="bank-account" className="text-sm font-semibold text-slate-700">
                  Company Bank Account (Payment Destination) <span className="text-red-500">*</span>
                </Label>
                {loadingBankAccounts ? (
                  <div className="flex items-center justify-center p-4 border rounded-lg">
                    <MoonLoader size="sm" className="mr-2" color="teal" />
                    <span className="text-sm text-gray-600"><TranslatedText text="Loading bank accounts..." /></span>
                  </div>
                ) : bankAccounts.length > 0 ? (
                  <Select value={selectedBankAccount} onValueChange={setSelectedBankAccount}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={tSync("Select company bank account to receive payment")} />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex flex-col py-1">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-slate-900">{account.bankName}</span>
                              {account.balance && (
                                <span className="text-sm font-medium text-green-600">
                                  {Number(account.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-slate-500">
                              {account.accountNumber} • {account.accountType || <TranslatedText text="Account" />}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                    <p className="text-sm text-red-600"><TranslatedText text="No bank accounts found. Please add bank accounts in the Banking section." /></p>
                  </div>
                )}
                <div className="text-xs text-slate-500">
                  <TranslatedText text="This is where the customer's payment will be deposited" />
                </div>
              </div>
            )}

            {/* Customer Payment Bank Information */}
            {paymentMethod !== 'cash' && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-700">
                  Customer Payment Bank Information (Optional)
                </Label>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Customer's Bank</Label>
                  <Select 
                    value={selectedCustomerBank} 
                    onValueChange={(value) => setSelectedCustomerBank(value)}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder={tSync("Select customer's bank (optional)")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <div className="p-2 border-b">
                        <Input
                          placeholder={tSync("Search customer's bank...")}
                          value={customerBankSearchTerm}
                          onChange={(e) => handleCustomerBankSearch(e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredCustomerBanks.length > 0 ? (
                          filteredCustomerBanks.map((bank) => (
                            <SelectItem key={bank} value={bank}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span>{bank}</span>
                              </div>
                            </SelectItem>
                          ))
                        ) : (
                          <div className="p-2 text-sm text-slate-500 text-center">
                            No banks found matching "{customerBankSearchTerm}"
                          </div>
                        )}
                      </div>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-slate-500">
                  Optional: Record which bank the customer is paying from for reference
                </div>
              </div>
            )}

            {/* Cash Payment Input */}
            {paymentMethod === 'cash' && (
              <div className="space-y-2">
                <Input
                  type="number"
                  placeholder={tSync("Cash received")}
                  value={cashReceived || ''}
                  onChange={(e) => setCashReceived(parseFloat(e.target.value) || 0)}
                  className="h-10 text-center"
                />
                {cashReceived > total && (
                  <div className="text-center p-2 bg-green-50 rounded-lg">
                    <div className="text-green-700 font-medium text-sm">
                      Change: {formatCurrency(cashReceived - total)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Process Button */}
            <Button
              onClick={processPayment}
              disabled={processing || createInvoiceMutation.isPending || (paymentMethod !== 'cash' && !selectedBankAccount)}
              className="w-full h-10 font-medium"
            >
              {processing || createInvoiceMutation.isPending ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  <TranslatedText text="Processing Payment..." />
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  <TranslatedText text="Complete Sale" />
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-base"><TranslatedText text="Apply Discount" /></DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={discountType === 'percent' ? 'default' : 'outline'}
                onClick={() => setDiscountType('percent')}
                size="sm"
              >
                <TranslatedText text="Percentage" />
              </Button>
              <Button
                variant={discountType === 'amount' ? 'default' : 'outline'}
                onClick={() => setDiscountType('amount')}
                size="sm"
              >
                <TranslatedText text="Amount" />
              </Button>
            </div>
            
            <Input
              type="number"
              placeholder={tSync(discountType === 'percent' ? 'Discount %' : 'Discount amount')}
              value={globalDiscount || ''}
              onChange={(e) => setGlobalDiscount(parseFloat(e.target.value) || 0)}
              className="h-10 text-center"
            />
            
            <Button
              onClick={() => setShowDiscountDialog(false)}
              className="w-full h-9"
              size="sm"
            >
              <TranslatedText text="Apply Discount" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center">
              <Receipt className="w-5 h-5 mr-2" />
              <TranslatedText text="Transaction Complete" />
            </DialogTitle>
          </DialogHeader>
          
          {receiptData && (
            <div className="space-y-4">
              {/* Receipt Preview */}
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <ReceiptComponent data={receiptData} className="receipt-component" />
              </div>
              
              {/* Receipt Actions */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={async () => {
                    try {
                      const receiptElement = document.querySelector('.receipt-component') as HTMLElement
                      if (receiptElement) {
                        const success = await ReceiptManager.printReceipt(receiptElement, {
                          paperSize: 'thermal_80mm'
                        })
                        if (success) {
                          toast({
                            title: "Receipt Printed",
                            description: "Receipt has been sent to printer",
                          })
                        } else {
                          toast({
                            title: "Print Failed",
                            description: "Unable to print receipt",
                            variant: "destructive"
                          })
                        }
                      }
                    } catch (error) {
                      console.error('Print error:', error)
                      toast({
                        title: "Print Error",
                        description: "Failed to print receipt",
                        variant: "destructive"
                      })
                    }
                  }}
                  className="flex items-center justify-center"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  <TranslatedText text="Print" />
                </Button>
                
                {/* Email Receipt Button - Hidden */}
                {false && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setReceiptEmailTo(receiptData.customer?.email || '')
                      setShowReceiptEmailDialog(true)
                    }}
                    className="flex items-center justify-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    <TranslatedText text="Email" />
                  </Button>
                )}
              </div>
              
              {/* Close Button */}
              <Button
                onClick={() => setShowReceiptDialog(false)}
                className="w-full"
                variant="outline"
              >
                <TranslatedText text="Close" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Information Modal */}
      <Dialog open={!!showProductInfo} onOpenChange={() => setShowProductInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center">
              <Info className="w-5 h-5 mr-2" />
              <TranslatedText text="Product Details" />
            </DialogTitle>
          </DialogHeader>
          
          {showProductInfo && (
            <div className="space-y-4">
              {/* Product Header */}
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  isService(showProductInfo) 
                    ? 'bg-gradient-to-br from-purple-100 to-purple-200' 
                    : 'bg-gradient-to-br from-blue-100 to-blue-200'
                }`}>
                  {typeof getProductIcon(showProductInfo) === 'string' ? (
                    <span className="text-xl">{getProductIcon(showProductInfo)}</span>
                  ) : (
                    <Package className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{showProductInfo.name}</h3>
                  <p className="text-sm text-slate-600">{showProductInfo.categoryObject?.name || 'Uncategorized'}</p>
                  <div className={`text-lg font-bold ${isService(showProductInfo) ? 'text-purple-600' : 'text-green-600'}`}>
                    {formatCurrency((showProductInfo.unitPrice && showProductInfo.unitPrice > 0) ? showProductInfo.unitPrice : (showProductInfo.costPrice && showProductInfo.costPrice > 0 ? showProductInfo.costPrice : 0), 'Frw')}
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-600"><TranslatedText text="SKU" />:</span>
                  <div className="font-medium">{showProductInfo.sku || tSync('N/A')}</div>
                </div>
                <div>
                  <span className="text-slate-600"><TranslatedText text="Barcode" />:</span>
                  <div className="font-medium">{showProductInfo.barcode || tSync('N/A')}</div>
                </div>
                <div>
                  <span className="text-slate-600"><TranslatedText text="Type" />:</span>
                  <div className="font-medium">{isService(showProductInfo) ? <TranslatedText text="Service" /> : <TranslatedText text="Product" />}</div>
                </div>
                <div>
                  <span className="text-slate-600"><TranslatedText text="Status" />:</span>
                  <div className="font-medium">{showProductInfo.status}</div>
                </div>
              </div>

              {/* Stock Information */}
              <div className="bg-slate-50 rounded-lg p-3">
                <h4 className="font-medium text-slate-900 mb-2">
                  {isService(showProductInfo) ? <TranslatedText text="Availability" /> : <TranslatedText text="Stock Information" />}
                </h4>
                <div className="text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">
                      {isService(showProductInfo) ? <TranslatedText text="Available Slots" /> : <TranslatedText text="Current Stock" />}:
                    </span>
                    <span className="font-medium">{showProductInfo.availableQuantity || showProductInfo.stockQuantity || 0}</span>
                  </div>
                  {!isService(showProductInfo) && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-600"><TranslatedText text="Minimum Stock" />:</span>
                        <span className="font-medium">{showProductInfo.minStockLevel || tSync('Not set')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600"><TranslatedText text="Unit of Measure" />:</span>
                        <span className="font-medium"><TranslatedText text="Each" /></span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Description */}
              {showProductInfo.description && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2"><TranslatedText text="Description" /></h4>
                  <p className="text-sm text-slate-600">{showProductInfo.description}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    addToCart(showProductInfo)
                    setShowProductInfo(null)
                  }}
                  disabled={showProductInfo.status !== 'ACTIVE' || (showProductInfo.availableQuantity || showProductInfo.stockQuantity || 0) <= 0}
                  className="flex-1"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="Add to Cart" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => toggleFavorite(showProductInfo.id)}
                  className={favoriteProducts.includes(showProductInfo.id) ? 'bg-amber-50 text-amber-600' : ''}
                >
                  <Star className="w-4 h-4" fill={favoriteProducts.includes(showProductInfo.id) ? "currentColor" : "none"} />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Barcode Scanner Dialog - Hidden */}
      {false && <Dialog open={showBarcodeDialog} onOpenChange={closeBarcodeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scan className="w-5 h-5" />
                Barcode Scanner
              </div>
              <div className="text-xs text-slate-500 font-normal">
                Press Ctrl+B to open • ESC to close
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Camera Preview Section */}
            <div className="space-y-3">
              <div className="bg-slate-100 rounded-lg aspect-video flex items-center justify-center relative overflow-hidden">
                {cameraStream ? (
                  <video
                    ref={(video) => {
                      if (video && cameraStream) {
                        video.srcObject = cameraStream
                        video.play()
                      }
                    }}
                    className="w-full h-full object-cover"
                    autoPlay
                    playsInline
                    muted
                  />
                ) : (
                  <div className="text-center p-4">
                    {scannerError ? (
                      <div className="text-red-600 space-y-2">
                        <AlertCircle className="w-12 h-12 mx-auto" />
                        <p className="text-sm">{scannerError}</p>
                      </div>
                    ) : (
                      <div className="text-slate-500 space-y-2">
                        <Camera className="w-12 h-12 mx-auto" />
                        <p className="text-sm">Camera not active</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Camera Controls Overlay */}
                <div className="absolute bottom-2 left-2 right-2 flex gap-2">
                  {!cameraStream ? (
                    <Button
                      onClick={startCamera}
                      disabled={isScanning}
                      className="flex-1"
                      size="sm"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Start Camera
                    </Button>
                  ) : (
                    <Button
                      onClick={stopCamera}
                      variant="destructive"
                      className="flex-1"
                      size="sm"
                    >
                      <VideoOff className="w-4 h-4 mr-2" />
                      Stop Camera
                    </Button>
                  )}
                </div>
              </div>
              
              {cameraStream && (
                <div className="text-center p-2 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2 text-blue-700 text-sm">
                    <Info className="w-4 h-4" />
                    Point camera at barcode to scan automatically
                  </div>
                </div>
              )}
            </div>

            {/* Manual Input Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Keyboard className="w-4 h-4" />
                Manual Entry
              </div>
              
              <div className="flex gap-2">
                <Input
                  placeholder={tSync("Enter barcode or SKU...")}
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      processBarcode(manualBarcode)
                    }
                  }}
                  className="flex-1"
                  autoFocus={!cameraStream}
                />
                <Button
                  onClick={() => processBarcode(manualBarcode)}
                  disabled={!manualBarcode.trim()}
                >
                  <Check className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Test Barcodes */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700">Test Barcodes</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  { code: "1234567890123", name: "Headphones" },
                  { code: "1234567890124", name: "Office Chair" },
                  { code: "1234567890125", name: "USB-C Hub" },
                  { code: "1234567890126", name: "Standing Desk" }
                ].map((item) => (
                  <Button
                    key={item.code}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setManualBarcode(item.code)
                      processBarcode(item.code)
                    }}
                    className="h-8 text-xs justify-start"
                  >
                    <div className="truncate">
                      <div className="font-mono">{item.code}</div>
                      <div className="text-slate-500">{item.name}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={closeBarcodeDialog}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // Clear and focus manual input
                  setManualBarcode('')
                  const input = document.querySelector('input[placeholder*="barcode"]') as HTMLInputElement
                  if (input) input.focus()
                }}
                variant="outline"
                className="flex-1"
              >
                Clear
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>}

      {/* Send Invoice Email Dialog */}
      <Dialog open={sendEmailOpen} onOpenChange={(open) => {
        setSendEmailOpen(open)
        if (!open) {
          setSendEmailInvoiceId(null)
          setSendEmailTo('')
          setSendEmailLoading(false)
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send Invoice Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                placeholder="customer@example.com"
                value={sendEmailTo}
                onChange={(e) => setSendEmailTo(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
              Invoice PDF will be attached to the email automatically.
            </div>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setSendEmailOpen(false)}
              disabled={sendEmailLoading}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleSendInvoiceEmail}
              disabled={sendEmailLoading || !sendEmailTo.trim()}
            >
              {sendEmailLoading ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice View Modal */}
      <Dialog open={showInvoiceViewDialog} onOpenChange={(open) => {
        setShowInvoiceViewDialog(open)
        if (!open) {
          setSelectedInvoiceForView(null)
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ maxWidth: 'calc(100vw - 300px)' }}>
          <DialogHeader className="pb-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                <Eye className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-slate-900">Invoice Details</DialogTitle>
                <DialogDescription className="text-slate-600 mt-1">
                  {selectedInvoiceForView ? `Invoice ${selectedInvoiceForView.invoiceNumber}` : 'Loading invoice details...'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {detailedInvoice && (
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Invoice Information</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Invoice Number:</span>
                      <span className="font-medium">{detailedInvoice.invoiceNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Issue Date:</span>
                      <span className="font-medium">{new Date(detailedInvoice.issueDate).toLocaleDateString()}</span>
                    </div>
                    {detailedInvoice.dueDate && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Due Date:</span>
                        <span className="font-medium">{new Date(detailedInvoice.dueDate).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-600">Status:</span>
                      <Badge 
                        variant={
                          detailedInvoice.status === "paid" ? "default" :
                          detailedInvoice.dueDate && new Date(detailedInvoice.dueDate) < new Date() && detailedInvoice.balanceDue > 0 ? "destructive" : 
                          "secondary"
                        }
                      >
                        {detailedInvoice.status.charAt(0).toUpperCase() + detailedInvoice.status.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Currency:</span>
                      <span className="font-medium">
                        {(() => {
                          const customer = allCustomers.find(c => c.id === detailedInvoice.customerId);
                          const currency = customer?.currency || detailedInvoice.currency || 'Frw';
                          return currency === 'USD' ? 'Frw' : currency;
                        })()}
                      </span>
                    </div>
                    
                    {/* Reconciliation Status */}
                    {posInvoiceReconciliationStatus && (
                      <div className="pt-3 mt-3 border-t border-slate-300">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700">Reconciliation Status:</span>
                          {posInvoiceReconciliationStatus.isReconciled ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Reconciled
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Not Reconciled</Badge>
                          )}
                        </div>
                        {posInvoiceReconciliationStatus.isReconciled && (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-slate-600">
                              {posInvoiceReconciliationStatus.reconciledPayments} of {posInvoiceReconciliationStatus.totalPayments} payment{posInvoiceReconciliationStatus.totalPayments !== 1 ? 's' : ''} reconciled
                            </p>
                            {posInvoiceReconciliationStatus.bankTransactions.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-200">
                                <p className="text-xs font-medium text-slate-700 mb-1">Bank Transactions:</p>
                                {posInvoiceReconciliationStatus.bankTransactions.map((bt, idx) => (
                                  <div key={idx} className="text-xs text-slate-600 flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                    {formatCurrency(bt.amount, detailedInvoice.currency || 'Frw')} - {new Date(bt.date).toLocaleDateString()}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Customer Information</h3>
                  {(() => {
                    const customer = customers.find(c => c.id === detailedInvoice.customerId)
                    return customer ? (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Name:</span>
                          <span className="font-medium">{customer.name}</span>
                        </div>
                        {customer.email && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Email:</span>
                            <span className="font-medium">{customer.email}</span>
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Phone:</span>
                            <span className="font-medium">{customer.phone}</span>
                          </div>
                        )}
                        {customer.address && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Address:</span>
                            <span className="font-medium">{customer.address}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500">Customer information not available</p>
                    )
                  })()}
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">Financial Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Total Amount:</span>
                      <span className="font-semibold text-lg">
                        {(() => {
                          const customer = allCustomers.find(c => c.id === detailedInvoice.customerId)
                          const currency = customer?.currency || detailedInvoice.currency || 'Frw'
                          const displayCurrency = currency === 'USD' ? 'Frw' : currency
                          const converted = convertedInvoiceAmounts[detailedInvoice.id]
                          const totalAmountToDisplay = currency === 'USD' && converted ? converted.totalAmount : detailedInvoice.totalAmount
                          return formatCurrency(totalAmountToDisplay, displayCurrency)
                        })()}
                      </span>
                    </div>
                    {detailedInvoice.balanceDue > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Balance Due:</span>
                        <span className="font-semibold text-amber-600">
                          {(() => {
                            const customer = allCustomers.find(c => c.id === detailedInvoice.customerId)
                            const currency = customer?.currency || detailedInvoice.currency || 'Frw'
                            const displayCurrency = currency === 'USD' ? 'Frw' : currency
                            const converted = convertedInvoiceAmounts[detailedInvoice.id]
                            const balanceDueToDisplay = currency === 'USD' && converted ? converted.balanceDue : detailedInvoice.balanceDue
                            return formatCurrency(balanceDueToDisplay, displayCurrency)
                          })()}
                        </span>
                      </div>
                    )}
                    {detailedInvoice.balanceDue === 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Payment Status:</span>
                        <span className="font-semibold text-green-600">Fully Paid</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    {detailedInvoice.dueDate && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Days Until Due:</span>
                        <span className={`font-medium ${
                          new Date(detailedInvoice.dueDate) < new Date() ? 'text-red-600' : 'text-slate-900'
                        }`}>
                          {Math.ceil((new Date(detailedInvoice.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Product Line Items */}
              {detailedInvoice.lines && detailedInvoice.lines.length > 0 ? (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Products & Services</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>Unit Price</TableHead>
                          <TableHead>Discount</TableHead>
                          <TableHead>Tax Rate</TableHead>
                          <TableHead className="text-right">Line Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailedInvoice.lines.map((line, index) => (
                          <TableRow key={line.id || index}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                                  <Package className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                  <div className="font-medium text-slate-900">{line.description}</div>
                                  {line.product && (
                                    <div className="text-sm text-slate-500">
                                      SKU: {line.product.sku || 'N/A'} | 
                                      Category: {line.product.categoryObject?.name || 'Uncategorized'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{Number(line.quantity) || 0}</div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">
                                {(() => {
                                  const customer = allCustomers.find(c => c.id === detailedInvoice.customerId)
                                  const currency = customer?.currency || detailedInvoice.currency || 'Frw'
                                  const displayCurrency = currency === 'USD' ? 'Frw' : currency
                                  return formatCurrency(Number(line.unitPrice) || 0, displayCurrency)
                                })()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {(() => {
                                  const discountRate = Number(line.discountRate) || 0;
                                  const lineTotal = Number(line.quantity) * Number(line.unitPrice);
                                  const lineDiscountAmount = lineTotal * (discountRate / 100);
                                  // Check if invoice has global discount or line has discount
                                  const invoiceDiscountTotal = Number((detailedInvoice as any).discountAmount) || 0;
                                  const hasInvoiceDiscount = invoiceDiscountTotal > 0;
                                  // Get currency with USD to Frw conversion
                                  const customer = allCustomers.find(c => c.id === detailedInvoice.customerId);
                                  const currency = customer?.currency || detailedInvoice.currency || 'Frw';
                                  const displayCurrency = currency === 'USD' ? 'Frw' : currency;
                                  // Check if there's actual discount (either from discountRate, calculated, or invoice-level discount)
                                  if (discountRate > 0 || lineDiscountAmount > 0 || hasInvoiceDiscount) {
                                    return (
                                      <span className="text-green-600 font-medium">
                                        {discountRate > 0 ? `${discountRate}%` : (hasInvoiceDiscount ? 'Discount Applied' : formatCurrency(lineDiscountAmount, displayCurrency))}
                                      </span>
                                    );
                                  }
                                  return <span className="text-slate-400">No discount</span>;
                                })()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {(() => {
                                  const taxRate = Number(line.taxRate) || 0
                                  const lineTotal = Number(line.quantity) * Number(line.unitPrice)
                                  const lineTaxAmount = lineTotal * (taxRate / 100)
                                  // Check if invoice has global tax or line has tax
                                  const invoiceTaxTotal = Number((detailedInvoice as any).taxTotal) || Number((detailedInvoice as any).taxAmount) || 0
                                  const hasInvoiceTax = invoiceTaxTotal > 0
                                  // Check if there's actual tax (either from taxRate, calculated, or invoice-level tax)
                                  if (taxRate > 0 || lineTaxAmount > 0 || hasInvoiceTax) {
                                    return (
                                      <span className="text-blue-600 font-medium">
                                        {taxRate > 0 ? `${taxRate}%` : (hasInvoiceTax ? 'Tax Applied' : 'Tax Applied')}
                                      </span>
                                    )
                                  }
                                  return <span className="text-slate-400">No tax</span>
                                })()}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="font-semibold text-slate-900">
                                {(() => {
                                  const customer = allCustomers.find(c => c.id === detailedInvoice.customerId)
                                  const currency = customer?.currency || detailedInvoice.currency || 'Frw'
                                  const displayCurrency = currency === 'USD' ? 'Frw' : currency
                                  return formatCurrency(line.lineTotal || (Number(line.quantity) * Number(line.unitPrice)), displayCurrency)
                                })()}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Line Items Summary */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Subtotal:</span>
                          <span className="font-medium">
                            {(() => {
                              const customer = allCustomers.find(c => c.id === detailedInvoice.customerId)
                              const currency = customer?.currency || detailedInvoice.currency || 'Frw'
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency
                              return formatCurrency(detailedInvoice.lines.reduce((sum, line) => 
                                sum + (Number(line.quantity) * Number(line.unitPrice)), 0
                              ), displayCurrency)
                            })()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Discount:</span>
                          <span className="font-medium text-green-600">
                            {(() => {
                              const customer = allCustomers.find(c => c.id === detailedInvoice.customerId)
                              const currency = customer?.currency || detailedInvoice.currency || 'Frw'
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency
                              
                              // First, check invoice-level discount
                              const invoiceDiscountAmount = Number((detailedInvoice as any).discountAmount) || 0;
                              
                              if (invoiceDiscountAmount > 0) {
                                return `-${formatCurrency(invoiceDiscountAmount, displayCurrency)}`;
                              }
                              
                              // Calculate discount from line items
                              const lineDiscounts = detailedInvoice.lines.reduce((sum: number, line: any) => {
                                const discountRate = Number(line.discountRate) || 0;
                                
                                if (discountRate > 0) {
                                  const lineTotal = Number(line.quantity) * Number(line.unitPrice);
                                  return sum + (lineTotal * (discountRate / 100));
                                }
                                return sum;
                              }, 0);
                              
                              if (lineDiscounts > 0) {
                                return `-${formatCurrency(lineDiscounts, displayCurrency)}`;
                              }
                              
                              return formatCurrency(0, displayCurrency);
                            })()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Tax:</span>
                          <span className="font-medium text-blue-600">
                            {(() => {
                              const customer = allCustomers.find(c => c.id === detailedInvoice.customerId)
                              const currency = customer?.currency || detailedInvoice.currency || 'Frw'
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency
                              
                              // First, try to use invoice-level tax
                              const invoiceTaxTotal = Number((detailedInvoice as any).taxTotal) || Number((detailedInvoice as any).taxAmount) || 0
                              
                              if (invoiceTaxTotal > 0) {
                                return formatCurrency(invoiceTaxTotal, displayCurrency)
                              }
                              
                              // Calculate tax from line items - try multiple methods
                              const calculatedTax = detailedInvoice.lines.reduce((sum, line) => {
                                // Method 1: Use taxRate if available
                                const taxRate = Number(line.taxRate) || 0;
                                if (taxRate > 0) {
                                  const lineSubtotal = Number(line.quantity) * Number(line.unitPrice);
                                  return sum + (lineSubtotal * (taxRate / 100));
                                }
                                
                                // Method 2: Calculate from lineTotal difference (if lineTotal includes tax)
                                const lineSubtotal = Number(line.quantity) * Number(line.unitPrice);
                                const lineTotal = Number(line.lineTotal) || lineSubtotal;
                                const lineTax = lineTotal - lineSubtotal;
                                
                                // Method 3: Use line.taxAmount if available
                                const lineTaxAmount = Number((line as any).taxAmount) || 0;
                                
                                // Use the first available method
                                return sum + (lineTaxAmount > 0 ? lineTaxAmount : (lineTax > 0 ? lineTax : 0));
                              }, 0)
                              
                              return formatCurrency(calculatedTax, displayCurrency)
                            })()}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Items Count:</span>
                          <span className="font-medium">
                            {detailedInvoice.lines.length} items
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Quantity:</span>
                          <span className="font-medium">
                            {detailedInvoice.lines.reduce((sum, line) => sum + Number(line.quantity), 0)} units
                          </span>
                        </div>
                        <div className="flex justify-between font-semibold text-lg pt-2 border-t border-slate-200">
                          <span className="text-slate-900">Grand Total:</span>
                          <span className="text-slate-900">
                            {(() => {
                              const customer = allCustomers.find(c => c.id === detailedInvoice.customerId)
                              const currency = customer?.currency || detailedInvoice.currency || 'Frw'
                              const displayCurrency = currency === 'USD' ? 'Frw' : currency
                              const converted = convertedInvoiceAmounts[detailedInvoice.id]
                              const totalAmountToDisplay = currency === 'USD' && converted ? converted.totalAmount : detailedInvoice.totalAmount
                              return formatCurrency(totalAmountToDisplay, displayCurrency)
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900">Product Details</h3>
                      <p className="text-blue-700 text-sm">
                        This invoice was created via URUTIQ-accounting systems. Product line items are stored in the invoice but may not be fully loaded in this view.
                      </p>
                      <div className="mt-2 text-xs text-blue-600">
                        Debug: Invoice has {detailedInvoice.lines?.length || 0} line items
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => handleDownloadInvoicePdf(detailedInvoice.id, detailedInvoice.invoiceNumber)}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => {
                    const customer = customers.find(c => c.id === detailedInvoice.customerId)
                    setSendEmailInvoiceId(detailedInvoice.id)
                    setSendEmailTo(customer?.email || '')
                    setSendEmailOpen(true)
                    setShowInvoiceViewDialog(false)
                  }}
                  className="flex-1"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </Button>

              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Email Dialog */}
    <Dialog open={showReceiptEmailDialog} onOpenChange={(open) => {
      setShowReceiptEmailDialog(open)
      if (!open) {
        setReceiptEmailTo('')
        setIsSendingReceiptEmail(false)
      }
    }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Email Receipt</DialogTitle>
            <DialogDescription>Enter the recipient email address to send the receipt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="receipt-email-to">Email Address</Label>
            <Input 
              id="receipt-email-to" 
              type="email" 
              placeholder="customer@example.com" 
              value={receiptEmailTo} 
              onChange={(e) => setReceiptEmailTo(e.target.value)} 
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowReceiptEmailDialog(false)
              setReceiptEmailTo('')
              setIsSendingReceiptEmail(false)
            }}>
              Cancel
            </Button>
            <Button 
              disabled={!receiptEmailTo || !receiptData || isSendingReceiptEmail}
              onClick={async () => {
                if (!receiptEmailTo || !receiptData || isSendingReceiptEmail) return
                
                setIsSendingReceiptEmail(true)
                
                try {
                  const success = await ReceiptManager.emailReceipt(receiptData, {
                    to: receiptEmailTo,
                    subject: `Receipt ${receiptData.invoiceNumber}`,
                    message: 'Thank you for your purchase! Your receipt is attached.'
                  })
                  
                  if (success) {
                    toast({
                      title: "Receipt Emailed",
                      description: `Receipt sent to ${receiptEmailTo}`,
                    })
                    setShowReceiptEmailDialog(false)
                    setReceiptEmailTo('')
                  } else {
                    toast({
                      title: "Email Failed",
                      description: "Unable to send receipt via email",
                      variant: "destructive"
                    })
                  }
                } catch (error) {
                  console.error('Email error:', error)
                  toast({
                    title: "Email Error",
                    description: "Failed to send receipt",
                    variant: "destructive"
                  })
                } finally {
                  setIsSendingReceiptEmail(false)
                }
              }}
            >
              {isSendingReceiptEmail ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Receipt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Edit Dialog - Full Sales.tsx Functionality */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="!max-w-7xl max-h-[95vh] w-[95vw] md:max-h-[90vh] md:w-[90vw] flex flex-col p-0">
          <DialogHeader className="pb-6 border-b border-slate-200 px-6 pt-6 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-slate-900">
                  {editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}
                </DialogTitle>
                <DialogDescription className="text-slate-600 mt-1">
                  {editingInvoice ? 'Update the invoice details below' : 'Fill in the details below to create a professional invoice'}
                </DialogDescription>
              </div>
            </div>
            {invoiceError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {invoiceError}
              </div>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0 px-6 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-6">
              {/* Left Column - Basic Information */}
              <div className="md:col-span-1 lg:col-span-1 space-y-6">
                {/* Customer Selection */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Customer Information
                  </h3>
                  <div className="space-y-2">
                    <Label htmlFor="inv-customer" className="text-sm font-medium text-slate-700">Select Customer *</Label>
                    <Select value={invoiceForm.customerId} onValueChange={(newCustomerId) => {
                      setInvoiceForm(prev => ({ ...prev, customerId: newCustomerId }))
                    }}>
                      <SelectTrigger className="h-10 border-slate-300 w-full">
                        <SelectValue placeholder={tSync("Choose a customer")} />
                      </SelectTrigger>
                      <SelectContent>
                        {allCustomers.map((customer: any) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} ({customer.currency || 'Frw'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Invoice Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="inv-number" className="text-sm font-medium text-slate-700">Invoice Number *</Label>
                      <Input
                        id="inv-number"
                        value={invoiceForm.invoiceNumber} 
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceNumber: e.target.value })} 
                        placeholder={tSync("e.g., INV-001")}
                        className="h-10 border-slate-300 w-full"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="inv-currency" className="text-sm font-medium text-slate-700">Currency</Label>
                      <Select value={invoiceForm.currency} onValueChange={(newCurrency) => {
                        setInvoiceForm(prev => ({ ...prev, currency: newCurrency }))
                      }}>
                        <SelectTrigger className="h-10 border-slate-300 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD - US Dollar</SelectItem>
                          <SelectItem value="EUR">EUR - Euro</SelectItem>
                          <SelectItem value="GBP">GBP - British Pound</SelectItem>
                          <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="inv-issue" className="text-sm font-medium text-slate-700">Issue Date *</Label>
                        <Input
                          id="inv-issue"
                          type="date"
                          value={invoiceForm.issueDate} 
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, issueDate: e.target.value })} 
                          className="h-10 border-slate-300"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inv-due" className="text-sm font-medium text-slate-700">Due Date</Label>
                        <Input
                          id="inv-due"
                          type="date"
                          value={invoiceForm.dueDate || ''} 
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })} 
                          className="h-10 border-slate-300"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="inv-shipping" className="text-sm font-medium text-slate-700">Shipping Amount</Label>
                      <Input
                        id="inv-shipping"
                        type="number"
                        step="0.01"
                        value={invoiceForm.shipping} 
                        onChange={(e) => {
                          const shipping = parseFloat(e.target.value) || 0
                          const totals = recalcTotals(invoiceForm.lines, invoiceForm.discount, shipping)
                          setInvoiceForm({ ...invoiceForm, shipping, ...totals })
                        }}
                        placeholder={tSync("0.00")}
                        className="h-10 border-slate-300"
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Discount</Label>
                      <div className="flex gap-2">
                        <Select value={invoiceForm.discountMode} onValueChange={(dm) => { 
                          setInvoiceForm(prev => ({ ...prev, discountMode: dm as 'amount'|'percent' }))
                        }}>
                          <SelectTrigger className="w-20 h-10 border-slate-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="amount">$</SelectItem>
                            <SelectItem value="percent">%</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={tSync("0")}
                          value={invoiceForm.discount} 
                          onChange={(e) => {
                            const discount = parseFloat(e.target.value) || 0
                            const totals = recalcTotals(invoiceForm.lines, discount, invoiceForm.shipping)
                            setInvoiceForm({ ...invoiceForm, discount, ...totals })
                          }}
                          className="h-10 border-slate-300"
                        />
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Discount {invoiceForm.discountMode === 'percent' ? '(%)' : ''}
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Tax Mode</Label>
                      <Select value={invoiceForm.taxMode} onValueChange={(taxMode) => { 
                        setInvoiceForm(prev => ({ ...prev, taxMode: taxMode as 'per_line'|'global' }))
                      }}>
                        <SelectTrigger className="h-10 border-slate-300 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="per_line">Per Line Item</SelectItem>
                          <SelectItem value="global">Global Tax Rate</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {invoiceForm.taxMode === 'global' && (
                      <div>
                        <Label htmlFor="inv-global-tax" className="text-sm font-medium text-slate-700">Global Tax Rate (%)</Label>
                        <Input
                          id="inv-global-tax"
                          type="number"
                          step="0.01"
                          value={invoiceForm.globalTaxRate} 
                          onChange={(e) => {
                            const globalTaxRate = parseFloat(e.target.value) || 0
                            const totals = recalcTotals(invoiceForm.lines, invoiceForm.discount, invoiceForm.shipping, 'global', globalTaxRate)
                            setInvoiceForm({ ...invoiceForm, globalTaxRate, ...totals })
                          }}
                          placeholder={tSync("0.00")}
                          className="h-10 border-slate-300"
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="inv-notes" className="text-sm font-medium text-slate-700">Notes</Label>
                      <Input
                        id="inv-notes"
                        value={invoiceForm.notes || ''} 
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} 
                        placeholder={tSync("Additional notes...")}
                        className="h-10 border-slate-300"
                      />
                    </div>

                    <div>
                      <Label htmlFor="inv-terms" className="text-sm font-medium text-slate-700">Terms & Conditions</Label>
                      <Input
                        id="inv-terms"
                        value={invoiceForm.terms || ''} 
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, terms: e.target.value })} 
                        placeholder={tSync("Payment terms and conditions...")}
                        className="h-10 border-slate-300"
                      />
                    </div>

                    <div>
                      <Label htmlFor="inv-payment-terms" className="text-sm font-medium text-slate-700">Payment Terms</Label>
                      <Select value={invoiceForm.paymentTerms || 'Net 30'} onValueChange={(value) => setInvoiceForm(prev => ({ ...prev, paymentTerms: value }))}>
                        <SelectTrigger className="h-10 border-slate-300 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                          <SelectItem value="Net 15">Net 15</SelectItem>
                          <SelectItem value="Net 30">Net 30</SelectItem>
                          <SelectItem value="Net 60">Net 60</SelectItem>
                          <SelectItem value="Net 90">Net 90</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Middle Column - Line Items */}
              <div className="md:col-span-1 lg:col-span-2 space-y-6">
                <div className="bg-slate-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5 text-blue-600" />
                      Line Items
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const lines = [...invoiceForm.lines, { description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]
                        setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">Add products or services to your invoice</p>
                  
                  <div className="space-y-3">
                    {invoiceForm.lines.map((line, idx) => (
                      <div key={idx} className="grid grid-cols-6 gap-3 p-4 border border-slate-200 rounded-lg bg-white">
                        <div className="col-span-2">
                          <Label className="text-xs text-slate-500">Description</Label>
                          <div className="space-y-1">
                            <Input
                              placeholder={tSync("Item description")}
                              value={line.description}
                              onChange={(e) => {
                                const lines = [...invoiceForm.lines]
                                lines[idx] = { ...line, description: e.target.value }
                                setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                              }}
                              className="h-10 border-slate-300"
                            />
                            <Select value={line.productId || ""} onValueChange={(productId) => {
                              const product = allProducts.find((p: any) => p.id === productId)
                              if (product) {
                                // Use costPrice as fallback when unitPrice is 0 or missing
                                const sellingPrice = (product.price && product.price > 0) 
                                  ? product.price 
                                  : ((product.unitPrice && product.unitPrice > 0) 
                                    ? product.unitPrice 
                                    : (product.costPrice && product.costPrice > 0 ? product.costPrice : 0))
                                
                                const lines = [...invoiceForm.lines]
                                lines[idx] = { 
                                  ...line, 
                                  productId: productId,
                                  description: product.name,
                                  unitPrice: sellingPrice,
                                  taxRate: 0
                                }
                                setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                              }
                            }}>
                              <SelectTrigger className="h-10 border-slate-300">
                                <SelectValue placeholder={tSync("Or select from product catalog...")} />
                              </SelectTrigger>
                              <SelectContent>
                                {allProducts.map((product: any) => {
                                  // Use costPrice as fallback when unitPrice is 0 or missing
                                  const displayPrice = (product.price && product.price > 0) 
                                    ? product.price 
                                    : ((product.unitPrice && product.unitPrice > 0) 
                                      ? product.unitPrice 
                                      : (product.costPrice && product.costPrice > 0 ? product.costPrice : 0))
                                  return (
                                    <SelectItem key={product.id} value={product.id}>
                                      {product.name} - ${displayPrice}
                                    </SelectItem>
                                  )
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-xs text-slate-500">Qty</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.quantity}
                            onChange={(e) => {
                              const lines = [...invoiceForm.lines]
                              lines[idx] = { ...line, quantity: parseFloat(e.target.value) || 0 }
                              setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                            }}
                            className="h-10 border-slate-300"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-slate-500">Unit Price</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={line.unitPrice}
                            onChange={(e) => {
                              const lines = [...invoiceForm.lines]
                              lines[idx] = { ...line, unitPrice: parseFloat(e.target.value) || 0 }
                              setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                            }}
                            className="h-10 border-slate-300"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-slate-500">Discount</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={line.lineDiscount || 0}
                            onChange={(e) => {
                              const lines = [...invoiceForm.lines]
                              lines[idx] = { ...line, lineDiscount: parseFloat(e.target.value) || 0 }
                              setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                            }}
                            className="h-10 border-slate-300"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-slate-500">Tax %</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={line.taxRate}
                            disabled={invoiceForm.taxMode === 'global'} 
                            onChange={(e) => {
                              const lines = [...invoiceForm.lines]
                              lines[idx] = { ...line, taxRate: parseFloat(e.target.value) || 0 }
                              setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                            }}
                            className="h-10 border-slate-300"
                          />
                          <div className="text-xs text-slate-400 mt-1">
                            {invoiceForm.taxMode === 'global' ? 'global' : 'tax %'}
                          </div>
                        </div>
                        
                        <div className="flex items-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const lines = invoiceForm.lines.filter((_, i) => i !== idx)
                              setInvoiceForm({ ...invoiceForm, lines: lines.length ? lines : [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }], ...recalcTotals(lines.length ? lines : [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]) })
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="col-span-6 mt-2">
                          <div className="text-sm text-slate-600">
                            Line Total: ${((Number(line.quantity) || 0) * (Number(line.unitPrice) || 0) - (Number(line.lineDiscount) || 0)).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {invoiceForm.lines.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No line items added yet</p>
                        <Button
                          variant="outline"
                          className="mt-2"
                          onClick={() => {
                            const lines = [{ description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]
                            setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add First Item
                        </Button>
                      </div>
                    )}
                    
                    {invoiceForm.lines.length > 0 && (
                      <div className="flex items-center justify-between text-sm text-slate-600">
                        <span>
                          <span className="font-medium">{invoiceForm.lines.length}</span> line item{invoiceForm.lines.length !== 1 ? 's' : ''} added
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const lines = [...invoiceForm.lines, { description: "", quantity: 1, unitPrice: 0, taxRate: 0, productId: "", lineDiscount: 0 }]
                            setInvoiceForm({ ...invoiceForm, lines, ...recalcTotals(lines) })
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Another Item
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Invoice Summary */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-blue-600" />
                    Invoice Summary
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Subtotal:</span>
                      <span className="text-slate-900 font-semibold text-lg">${(Number(invoiceForm.subtotal) || 0).toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Tax:</span>
                      <span className="text-slate-900 font-semibold text-lg">${(Number(invoiceForm.taxTotal) || 0).toFixed(2)}</span>
                    </div>
                    
                    {(Number(invoiceForm.shipping) || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Shipping:</span>
                        <span className="text-slate-900 font-semibold text-lg">${(Number(invoiceForm.shipping) || 0).toFixed(2)}</span>
                      </div>
                    )}
                    
                    {(Number(invoiceForm.discount) || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Discount:</span>
                        <span className="text-red-600 font-semibold text-lg">-${(Number(invoiceForm.discount) || 0).toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="border-t border-slate-200 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-900 font-bold text-lg">Total:</span>
                        <span className="text-slate-900 font-bold text-2xl">${(Number(invoiceForm.totalAmount) || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="pt-6 border-t border-slate-200 px-6 pb-6 flex-shrink-0">
            <div className="flex items-center justify-between w-full">
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
              
              <Button 
                onClick={handleSaveInvoice}
                disabled={invoiceSaving}
                className="h-12 px-8 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {invoiceSaving ? (
                  <>
                    <MoonLoader size="sm" className="mr-2" color="teal" />
                    {editingInvoice ? 'Updating Invoice...' : 'Creating Invoice...'}
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-slate-900">Delete Invoice</DialogTitle>
                <DialogDescription className="text-sm text-slate-600 mt-1">
                  Are you sure you want to delete invoice "{invoiceToDelete?.invoiceNumber}"? This action can be undone.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              className="h-9"
              disabled={deletingInvoice}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteInvoice}
              disabled={deletingInvoice}
              className="h-9"
            >
              {deletingInvoice ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
